import { computed, DestroyRef, inject, Signal, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, take } from 'rxjs';
import { ChangelogEntry } from 'src/app/_models/changelog';
import { CreateExperiment } from 'src/app/_models/experiment-create';
import { Experiment } from 'src/app/_models/experiment';
import { ExperimentTemplate } from 'src/app/_models/experiment-template';
import { ExperimentUpdate } from 'src/app/_models/experiment-update';
import {
  ExperimentMode,
  ExperimentModeDisplay,
} from 'src/app/_models/experiment-mode';
import { FluorescenceChannel } from 'src/app/_models/fluorescence-channel';
import { FormBase } from 'src/app/_models/form-base';
import { Laboratory } from 'src/app/_models/laboratory';
import {
  EDIT_ACCESS_ROLE_LIST,
  LAB_ADMIN_ROLE_LIST,
  Role,
} from 'src/app/_models/role';
import { User } from 'src/app/_models/user';
import { ExperimentParameterRanges } from 'src/app/_helpers/experiment-min-max';
import { advancedNameRegex, clearFormArray } from 'src/app/_helpers/utils';
import { stepValidator } from 'src/app/_helpers/step-validator.validator';
import {
  convertBooleanToInputType,
  convertInputTypeToBoolean,
  convertSpargingStatus,
  convertSpecGainToString,
  Flask,
  getSpargingStatus,
  hasExperimentEditAccess,
  NOTIFICATION_TOOLTIP,
} from 'src/app/_helpers/experiment-utils';
import { AuthEventService } from 'src/app/_services/auth-event.service';
import { DeviceService } from 'src/app/_services/device.service';
import { ModalService } from 'src/app/_services/modal.service';
import { Module } from 'src/app/_models/module-enum';
import { ErrorObject } from 'src/app/_models/error';
import { LaboratoryService } from 'src/app/_services/laboratory.service';

export abstract class ExperimentFormBase {
  // ── Abstract members ──────────────────────────────────────────────────────

  abstract readonly entityId: Signal<number | null>;
  abstract fetchChangelog(): void;
  abstract getSavedEntity(): ExperimentTemplate | Experiment | null;

  // ── Protected hook methods ─────────────────────────────────────────────────

  protected onAfterExperimentModeChange(): void {}
  protected onAfterModuleCheckboxClicked(): void {}

  // ── Shared services ────────────────────────────────────────────────────────

  protected readonly destroyRef = inject(DestroyRef);
  protected readonly router = inject(Router);
  protected readonly formBuilder = inject(FormBuilder);
  protected readonly deviceService = inject(DeviceService);
  protected readonly labService = inject(LaboratoryService);
  protected readonly modalService = inject(ModalService);
  protected readonly authEventService = inject(AuthEventService);
  protected readonly route = inject(ActivatedRoute);

  // ── Enums ──────────────────────────────────────────────────────────────────

  readonly ExperimentMode = ExperimentMode;
  readonly Role = Role;
  readonly editAccessRoles = EDIT_ACCESS_ROLE_LIST;
  readonly labAdminRoles = LAB_ADMIN_ROLE_LIST;
  readonly notificationTooltip = NOTIFICATION_TOOLTIP;

  // ── Permissions ────────────────────────────────────────────────────────────

  laboratory = signal<Laboratory | null>(null);
  currentUser = signal<User | null>(null);
  hasEditAccess = computed(() =>
    hasExperimentEditAccess(this.currentUser()?.role),
  );

  // ── Module flags ───────────────────────────────────────────────────────────

  useFluorescence = signal<boolean>(false);
  useOxygen = signal<boolean>(false);
  usePH = signal<boolean>(false);
  useSparging = signal<boolean>(false);

  // ── Misc ───────────────────────────────────────────────────────────────────

  requiredDeviceModules = signal<number[]>([]);
  allowNavigation: Subject<boolean> = new Subject<boolean>();
  isProcessing = signal<boolean>(false);
  getLabUsersError = signal<ErrorObject | null>(null);

  // ── Fluorescence channel selection ─────────────────────────────────────────

  selectedFluorescenceChannels = signal<FluorescenceChannel[]>([]);
  fluorescenceChannels = signal<FluorescenceChannel[]>([]);
  fluorescenceChannelsLoading = signal<boolean>(false);
  fluorescenceChannelsError = signal<ErrorObject | null>(null);

  // ── Pagination ─────────────────────────────────────────────────────────────

  pageRequested = signal<number>(1);
  pageSize = signal<number>(5);
  totalCount = signal<number>(0);
  totalPages = signal<number>(0);
  currentPage = signal<number>(1);
  hasNext = signal<boolean>(false);
  hasPrevious = signal<boolean>(false);

  // ── Email Notifications ────────────────────────────────────────────────────

  labMemembersDropdownLoading = signal<boolean>(false);
  users = signal<User[]>([]);
  protected readonly notificationEmailValue = signal<string>('');
  readonly selectedNotificationUser = computed(
    () =>
      this.users().find((u: User) => u.email === this.notificationEmailValue()) ??
      null,
  );

  // ── Table vars ─────────────────────────────────────────────────────────────

  changelogList = signal<ChangelogEntry[]>([]);
  readonly changelogDisplayedColumns = ['date', 'note', 'change', 'user'];
  currentSort = signal<{ sortBy: string; sortDirection: string }>({
    sortBy: 'CreatedAt',
    sortDirection: 'asc',
  });

  // ── Parameter range constants ──────────────────────────────────────────────

  protected readonly minSampleTimeInMins =
    ExperimentParameterRanges.SAMPLE_TIME_MINS.min;
  protected readonly maxSampleTimeInMins =
    ExperimentParameterRanges.SAMPLE_TIME_MINS.max;
  protected readonly sampleTimeStep =
    ExperimentParameterRanges.SAMPLE_TIME_MINS.step;
  protected readonly minExperimentTimeInHours =
    ExperimentParameterRanges.EXPERIMENT_DURATION_HOURS.min;
  protected readonly maxExperimentTimeInHours =
    ExperimentParameterRanges.EXPERIMENT_DURATION_HOURS.max;
  protected readonly experimentTimeStep =
    ExperimentParameterRanges.EXPERIMENT_DURATION_HOURS.step;
  protected readonly minSpeed = ExperimentParameterRanges.SPEED_RPM.min;
  protected readonly maxSpeed = ExperimentParameterRanges.SPEED_RPM.max;
  protected readonly speedStep = ExperimentParameterRanges.SPEED_RPM.step;
  protected readonly minAmbientTemp =
    ExperimentParameterRanges.AMBIENT_TEMPERATURE.min;
  protected readonly maxAmbientTemp =
    ExperimentParameterRanges.AMBIENT_TEMPERATURE.max;
  protected readonly ambientTempStep =
    ExperimentParameterRanges.AMBIENT_TEMPERATURE.step;
  protected readonly minAmbientTempWarning =
    ExperimentParameterRanges.AMBIENT_TEMPERATURE.minAmbientTempWarning;
  protected readonly maxAmbientTempWarning =
    ExperimentParameterRanges.AMBIENT_TEMPERATURE.maxAmbientTempWarning;
  protected readonly minCultureTemp =
    ExperimentParameterRanges.CULTURE_TEMPERATURE.min;
  protected readonly maxCultureTemp =
    ExperimentParameterRanges.CULTURE_TEMPERATURE.max;
  protected readonly cultureTempStep =
    ExperimentParameterRanges.CULTURE_TEMPERATURE.step;
  protected readonly ambientTempWarningMessage =
    'Warning: Temperature is outside of recommended range';
  protected readonly minTargetOD = ExperimentParameterRanges.TARGET_OD.min;
  protected readonly maxTargetOD = ExperimentParameterRanges.TARGET_OD.max;
  protected readonly targetODStep = ExperimentParameterRanges.TARGET_OD.step;
  protected readonly minFlowRate = ExperimentParameterRanges.FLOW_RATE.min;
  protected readonly maxFlowRate = ExperimentParameterRanges.FLOW_RATE.max;
  protected readonly flowRateStep = ExperimentParameterRanges.FLOW_RATE.step;
  protected readonly minTargetPH = ExperimentParameterRanges.TARGET_PH.min;
  protected readonly maxTargetPH = ExperimentParameterRanges.TARGET_PH.max;
  protected readonly targetPHStep = ExperimentParameterRanges.TARGET_PH.step;
  protected readonly minPumpDuration =
    ExperimentParameterRanges.PUMP_DURATION_SECONDS.min;
  protected readonly maxPumpDuration =
    ExperimentParameterRanges.PUMP_DURATION_SECONDS.max;
  protected readonly pumpDurationStep =
    ExperimentParameterRanges.PUMP_DURATION_SECONDS.step;
  protected readonly minLEDIntensity =
    ExperimentParameterRanges.LED_INTENSITY.min;
  protected readonly maxLEDIntensity =
    ExperimentParameterRanges.LED_INTENSITY.max;
  protected readonly ledIntensityStep =
    ExperimentParameterRanges.LED_INTENSITY.step;
  protected readonly minIntegrationTime =
    ExperimentParameterRanges.INTEGRATION_TIME.min;
  protected readonly maxIntegrationTime =
    ExperimentParameterRanges.INTEGRATION_TIME.max;
  protected readonly integrationTimeStep =
    ExperimentParameterRanges.INTEGRATION_TIME.step;
  protected readonly minSalinity = ExperimentParameterRanges.SALINITY.min;
  protected readonly maxSalinity = ExperimentParameterRanges.SALINITY.max;
  protected readonly salinityStep = ExperimentParameterRanges.SALINITY.step;
  protected readonly minThreshold = ExperimentParameterRanges.OD_THRESHOLD.min;
  protected readonly maxThreshold = ExperimentParameterRanges.OD_THRESHOLD.max;
  protected readonly thresholdStep = ExperimentParameterRanges.OD_THRESHOLD.step;
  protected readonly minDilutionPeriod =
    ExperimentParameterRanges.DILUTION_PERIOD_MINS.min;
  protected readonly maxDilutionPeriod =
    ExperimentParameterRanges.DILUTION_PERIOD_MINS.max;
  protected readonly dilutionPeriodStep =
    ExperimentParameterRanges.DILUTION_PERIOD_MINS.step;
  protected readonly minDilutionFactor =
    ExperimentParameterRanges.DILUTION_FACTOR.min;
  protected readonly maxDilutionFactor =
    ExperimentParameterRanges.DILUTION_FACTOR.max;
  protected readonly dilutionFactorStep =
    ExperimentParameterRanges.DILUTION_FACTOR.step;
  protected minAirflow = ExperimentParameterRanges.SPARGING_AIRFLOW.min;
  protected maxAirflow = ExperimentParameterRanges.SPARGING_AIRFLOW.max;
  protected readonly airflowStep = ExperimentParameterRanges.SPARGING_AIRFLOW.step;
  protected readonly airflowWarningValue = ExperimentParameterRanges.SPARGING_AIRFLOW.warning;
  protected readonly airflowRateWarningMessage =
    ExperimentParameterRanges.SPARGING_AIRFLOW.warningMessage;

  // ── Shared FormBase field definitions ─────────────────────────────────────

  experimentModeField = new FormBase<ExperimentMode>({
    key: 'experimentMode',
    label: 'Select experiment mode',
    type: 'radio',
    placeholder: '',
    disabled: false,
    required: true,
    value: ExperimentMode.BatchCulture,
    options: [
      {
        key: ExperimentMode.BatchCulture,
        value: ExperimentModeDisplay[ExperimentMode.BatchCulture],
      },
      {
        key: ExperimentMode.Turbidostat,
        value: ExperimentModeDisplay[ExperimentMode.Turbidostat],
      },
      {
        key: ExperimentMode.Chemostat,
        value: ExperimentModeDisplay[ExperimentMode.Chemostat],
      },
      {
        key: ExperimentMode.PHControl,
        value: ExperimentModeDisplay[ExperimentMode.PHControl],
      },
      {
        key: ExperimentMode.PHControlDouble,
        value: ExperimentModeDisplay[ExperimentMode.PHControlDouble],
      },
      {
        key: ExperimentMode.PHControlChemostat,
        value: ExperimentModeDisplay[ExperimentMode.PHControlChemostat],
      },
      {
        key: ExperimentMode.Morbidostat,
        value: ExperimentModeDisplay[ExperimentMode.Morbidostat],
      },
    ],
  });

  moduleCheckboxesField = new FormBase<string>({
    key: 'moduleCheckboxes',
    label: 'Select modules',
    type: 'checkbox',
    placeholder: '',
    disabled: false,
    required: false,
    value: '',
    options: [
      { key: 'Oxygen', value: 'Oxygen' },
      { key: 'Fluorescence', value: 'Fluorescence' },
      { key: 'pH', value: 'pH' },
      { key: 'Sparging', value: 'Sparging' },
    ],
  });

  experimentNameField = new FormBase<string>({
    key: 'name',
    label: 'Experiment Name',
    type: 'text',
    placeholder: 'Experiment name',
    disabled: false,
    required: true,
    value: '',
    options: [],
  });

  organismField = new FormBase<string>({
    key: 'organism',
    label: 'Organism',
    type: 'text',
    placeholder: 'Organism name',
    disabled: false,
    required: true,
    value: '',
    options: [],
  });

  strainField = new FormBase<string>({
    key: 'strain',
    label: 'Strain',
    type: 'text',
    placeholder: 'Strain name',
    disabled: false,
    required: true,
    value: '',
    options: [],
  });

  mediaField = new FormBase<string>({
    key: 'media',
    label: 'Media',
    type: 'text',
    placeholder: 'Media name',
    disabled: false,
    required: true,
    value: '',
    options: [],
  });

  // Base Unit
  ambientTemperatureField = new FormBase<number>({
    key: 'ambientTemperature',
    label: 'Ambient Temperature (°C)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: true,
    value: ExperimentParameterRanges.AMBIENT_TEMPERATURE.default,
    options: [],
  });

  temperatureControlAField = new FormBase<boolean>({
    key: 'temperatureControlA',
    label: 'Temperature Control A',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: true,
    options: [],
  });

  temperatureControlBField = new FormBase<boolean>({
    key: 'temperatureControlB',
    label: 'Temperature Control B',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: true,
    options: [],
  });

  temperatureControlCField = new FormBase<boolean>({
    key: 'temperatureControlC',
    label: 'Temperature Control C',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: true,
    options: [],
  });

  temperatureControlDField = new FormBase<boolean>({
    key: 'temperatureControlD',
    label: 'Temperature Control D',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: true,
    options: [],
  });

  temperatureAField = new FormBase<number>({
    key: 'cultureTemperatureA',
    label: 'Culture Temperature A (°C)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.CULTURE_TEMPERATURE.default,
    options: [],
  });

  temperatureBField = new FormBase<number>({
    key: 'cultureTemperatureB',
    label: 'Culture Temperature B (°C)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.CULTURE_TEMPERATURE.default,
    options: [],
  });

  temperatureCField = new FormBase<number>({
    key: 'cultureTemperatureC',
    label: 'Culture Temperature C (°C)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.CULTURE_TEMPERATURE.default,
    options: [],
  });

  temperatureDField = new FormBase<number>({
    key: 'cultureTemperatureD',
    label: 'Culture Temperature D (°C)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.CULTURE_TEMPERATURE.default,
    options: [],
  });

  samplingTimeField = new FormBase<number>({
    key: 'samplingTimeField',
    label: 'Sampling Time (mins)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: true,
    value: ExperimentParameterRanges.SAMPLE_TIME_MINS.default,
    options: [],
  });

  experimentTimeField = new FormBase<number>({
    key: 'experimentTimeField',
    label: 'Experiment Time (hours)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: true,
    value: ExperimentParameterRanges.EXPERIMENT_DURATION_HOURS.default,
    options: [],
  });

  speedAField = new FormBase<number>({
    key: 'speedAField',
    label: 'Stirring speed - flask A (RPM)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: true,
    value: ExperimentParameterRanges.SPEED_RPM.default,
    options: [],
  });

  speedBField = new FormBase<number>({
    key: 'speedBField',
    label: 'Stirring speed - flask B (RPM)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: true,
    value: ExperimentParameterRanges.SPEED_RPM.default,
    options: [],
  });

  speedCField = new FormBase<number>({
    key: 'speedCField',
    label: 'Stirring speed - flask C (RPM)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: true,
    value: ExperimentParameterRanges.SPEED_RPM.default,
    options: [],
  });

  speedDField = new FormBase<number>({
    key: 'speedDField',
    label: 'Stirring speed - flask D (RPM)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: true,
    value: ExperimentParameterRanges.SPEED_RPM.default,
    options: [],
  });

  // If setStirringSpeedField is true, set stirring speed to 0
  setStirringSpeedAField = new FormBase<boolean>({
    key: 'setStirringSpeedAField',
    label: 'Set stirring speed on flask A to 0',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: false,
    options: [],
  });

  setStirringSpeedBField = new FormBase<boolean>({
    key: 'setStirringSpeedBField',
    label: 'Set stirring speed on flask B to 0',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: false,
    options: [],
  });

  setStirringSpeedCField = new FormBase<boolean>({
    key: 'setStirringSpeedCField',
    label: 'Set stirring speed on flask C to 0',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: false,
    options: [],
  });

  setStirringSpeedDField = new FormBase<boolean>({
    key: 'setStirringSpeedDField',
    label: 'Set stirring speed on flask D to 0',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: false,
    options: [],
  });

  // Turbidostat
  maintainODField = new FormBase<boolean>({
    key: 'maintainOD',
    label: 'Maintain Initial OD',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: false,
    options: [],
  });

  targetODAField = new FormBase<number>({
    key: 'targetODA',
    label: 'Target OD A',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.TARGET_OD.default,
    options: [],
  });

  targetODBField = new FormBase<number>({
    key: 'targetODB',
    label: 'Target OD B',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.TARGET_OD.default,
    options: [],
  });

  targetODCField = new FormBase<number>({
    key: 'targetODC',
    label: 'Target OD C',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.TARGET_OD.default,
    options: [],
  });

  targetODDField = new FormBase<number>({
    key: 'targetODD',
    label: 'Target OD D',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.TARGET_OD.default,
    options: [],
  });

  turbAActiveField = new FormBase<boolean>({
    key: 'turbAActive',
    label: 'Turbidostat Control A',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: true,
    options: [],
  });

  turbBActiveField = new FormBase<boolean>({
    key: 'turbBActive',
    label: 'Turbidostat Control B',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: true,
    options: [],
  });

  turbCActiveField = new FormBase<boolean>({
    key: 'turbCActive',
    label: 'Turbidostat Control C',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: true,
    options: [],
  });

  turbDActiveField = new FormBase<boolean>({
    key: 'turbDActive',
    label: 'Turbidostat Control D',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: true,
    options: [],
  });

  // Chemostat
  chemoAActiveField = new FormBase<boolean>({
    key: 'chemoAActive',
    label: 'Chemostat Control A',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: true,
    options: [],
  });

  chemoBActiveField = new FormBase<boolean>({
    key: 'chemoBActive',
    label: 'Chemostat Control B',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: true,
    options: [],
  });

  chemoCActiveField = new FormBase<boolean>({
    key: 'chemoCActive',
    label: 'Chemostat Control C',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: true,
    options: [],
  });

  chemoDActiveField = new FormBase<boolean>({
    key: 'chemoDActive',
    label: 'Chemostat Control D',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: true,
    options: [],
  });

  flowRateAField = new FormBase<number>({
    key: 'flowRateA',
    label: 'Flow Rate A (ml/h)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.FLOW_RATE.default,
    options: [],
  });

  flowRateBField = new FormBase<number>({
    key: 'flowRateB',
    label: 'Flow Rate B (ml/h)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.FLOW_RATE.default,
    options: [],
  });

  flowRateCField = new FormBase<number>({
    key: 'flowRateC',
    label: 'Flow Rate C (ml/h)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.FLOW_RATE.default,
    options: [],
  });

  flowRateDField = new FormBase<number>({
    key: 'flowRateD',
    label: 'Flow Rate D (ml/h)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.FLOW_RATE.default,
    options: [],
  });

  // pH Control
  phControlAField = new FormBase<boolean>({
    key: 'phControlA',
    label: 'pH Control A',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: true,
    options: [],
  });

  phControlBField = new FormBase<boolean>({
    key: 'phControlB',
    label: 'pH Control B',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: true,
    options: [],
  });

  phControlCField = new FormBase<boolean>({
    key: 'phControlC',
    label: 'pH Control C',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: true,
    options: [],
  });

  phControlDField = new FormBase<boolean>({
    key: 'phControlD',
    label: 'pH Control D',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: true,
    options: [],
  });

  targetPHAField = new FormBase<number>({
    key: 'targetPHA',
    label: 'Target pH A',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.TARGET_PH.default,
    options: [],
  });

  targetPHBField = new FormBase<number>({
    key: 'targetPHB',
    label: 'Target pH B',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.TARGET_PH.default,
    options: [],
  });

  targetPHCField = new FormBase<number>({
    key: 'targetPHC',
    label: 'Target pH C',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.TARGET_PH.default,
    options: [],
  });

  targetPHDField = new FormBase<number>({
    key: 'targetPHD',
    label: 'Target pH D',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.TARGET_PH.default,
    options: [],
  });

  inputTypeAField = new FormBase<string>({
    key: 'inputTypeA',
    label: 'Input Type A',
    type: 'radio',
    placeholder: '',
    disabled: false,
    required: false,
    value: 'AcidA',
    options: [
      { key: 'BaseA', value: 'Base' },
      { key: 'AcidA', value: 'Acid' },
    ],
  });

  inputTypeBField = new FormBase<string>({
    key: 'inputTypeB',
    label: 'Input Type B',
    type: 'select',
    placeholder: '',
    disabled: false,
    required: false,
    value: 'AcidB',
    options: [
      { key: 'BaseB', value: 'Base' },
      { key: 'AcidB', value: 'Acid' },
    ],
  });

  inputTypeCField = new FormBase<string>({
    key: 'inputTypeC',
    label: 'Input Type C',
    type: 'select',
    placeholder: '',
    disabled: false,
    required: false,
    value: 'AcidC',
    options: [
      { key: 'BaseC', value: 'Base' },
      { key: 'AcidC', value: 'Acid' },
    ],
  });

  inputTypeDField = new FormBase<string>({
    key: 'inputTypeD',
    label: 'Input Type D',
    type: 'select',
    placeholder: '',
    disabled: false,
    required: false,
    value: 'AcidD',
    options: [
      { key: 'BaseD', value: 'Base' },
      { key: 'AcidD', value: 'Acid' },
    ],
  });

  pumpDurationAField = new FormBase<number>({
    key: 'pumpDurationA',
    label: 'Pump Duration A (seconds)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.PUMP_DURATION_SECONDS.default,
    options: [],
  });

  pumpDurationBField = new FormBase<number>({
    key: 'pumpDurationB',
    label: 'Pump Duration B (seconds)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.PUMP_DURATION_SECONDS.default,
    options: [],
  });

  pumpDurationCField = new FormBase<number>({
    key: 'pumpDurationC',
    label: 'Pump Duration C (seconds)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.PUMP_DURATION_SECONDS.default,
    options: [],
  });

  pumpDurationDField = new FormBase<number>({
    key: 'pumpDurationD',
    label: 'Pump Duration D (seconds)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.PUMP_DURATION_SECONDS.default,
    options: [],
  });

  // pH 2xControl
  ph2ControlAField = new FormBase<boolean>({
    key: 'ph2ControlA',
    label: 'pH Control A',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: true,
    options: [],
  });

  ph2ControlBField = new FormBase<boolean>({
    key: 'ph2ControlB',
    label: 'pH Control B',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: true,
    options: [],
  });

  targetPH2AField = new FormBase<number>({
    key: 'target2PHA',
    label: 'Target pH A',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.TARGET_PH.default,
    options: [],
  });

  targetPH2BField = new FormBase<number>({
    key: 'target2PHB',
    label: 'Target pH B',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.TARGET_PH.default,
    options: [],
  });

  pumpDuration2AField = new FormBase<number>({
    key: 'pumpDuration2A',
    label: 'Pump Duration A (seconds)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.PUMP_DURATION_SECONDS.default,
    options: [],
  });

  pumpDuration2BField = new FormBase<number>({
    key: 'pHpumpDuration2B',
    label: 'Pump Duration B (seconds)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.PUMP_DURATION_SECONDS.default,
    options: [],
  });

  // pH + Chemostat
  phChemoControlAField = new FormBase<boolean>({
    key: 'pHChemoAActive',
    label: 'pH Chemo A',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: true,
    options: [],
  });

  phChemoControlBField = new FormBase<boolean>({
    key: 'pHChemoBActive',
    label: 'pH Chemo B',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: true,
    options: [],
  });

  targetPHChemoAField = new FormBase<number>({
    key: 'targetPHChemoA',
    label: 'Target pH A',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.TARGET_PH.default,
    options: [],
  });

  targetPHChemoBField = new FormBase<number>({
    key: 'targetPHChemoB',
    label: 'Target pH B',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.TARGET_PH.default,
    options: [],
  });

  pumpDurationChemoAField = new FormBase<number>({
    key: 'pumpDurationChemoA',
    label: 'Pump Duration A (seconds)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.PUMP_DURATION_SECONDS.default,
    options: [],
  });

  pumpDurationChemoBField = new FormBase<number>({
    key: 'pumpDurationChemoB',
    label: 'Pump Duration B (seconds)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.PUMP_DURATION_SECONDS.default,
    options: [],
  });

  inputTypeChemoAField = new FormBase<string>({
    key: 'inputChemoTypeA',
    label: 'Input Type A',
    type: 'radio',
    placeholder: '',
    disabled: false,
    required: false,
    value: 'ChemoAcidA',
    options: [
      { key: 'ChemoBaseA', value: 'Base' },
      { key: 'ChemoAcidA', value: 'Acid' },
    ],
  });

  inputTypeChemoBField = new FormBase<string>({
    key: 'inputChemoTypeB',
    label: 'Input Type B',
    type: 'select',
    placeholder: '',
    disabled: false,
    required: false,
    value: 'ChemoAcidB',
    options: [
      { key: 'ChemoBaseB', value: 'Base' },
      { key: 'ChemoAcidB', value: 'Acid' },
    ],
  });

  flowRatePHAField = new FormBase<number>({
    key: 'chemoFlowRateA',
    label: 'Flow Rate A (ml/h)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.FLOW_RATE.default,
    options: [],
  });

  flowRatePHBField = new FormBase<number>({
    key: 'chemoFlowRateB',
    label: 'Flow Rate B (ml/h)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.FLOW_RATE.default,
    options: [],
  });

  // Fluro
  led1OnField = new FormBase<boolean>({
    key: 'led1On',
    label: 'LED 1 Always On',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: false,
    options: [],
  });

  led2OnField = new FormBase<boolean>({
    key: 'led2On',
    label: 'LED 2 Always On',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: false,
    options: [],
  });

  led3OnField = new FormBase<boolean>({
    key: 'led3On',
    label: 'LED 3 Always On',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: false,
    options: [],
  });

  led1IntensityField = new FormBase<number>({
    key: 'led1Intensity',
    label: 'LED 1 Intensity',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.LED_INTENSITY.default,
    options: [],
  });

  led2IntensityField = new FormBase<number>({
    key: 'led2Intensity',
    label: 'LED 2 Intensity',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.LED_INTENSITY.default,
    options: [],
  });

  led3IntensityField = new FormBase<number>({
    key: 'led3Intensity',
    label: 'LED 3 Intensity',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.LED_INTENSITY.default,
    options: [],
  });

  integrationTimeAField = new FormBase<number>({
    key: 'integrationTimeA',
    label: 'Integration Time A (ms)',
    type: 'number',
    placeholder: '',
    disabled: true,
    required: false,
    value: ExperimentParameterRanges.INTEGRATION_TIME.default,
    options: [],
  });

  integrationTimeBField = new FormBase<number>({
    key: 'integrationTimeB',
    label: 'Integration Time B (ms)',
    type: 'number',
    placeholder: '',
    disabled: true,
    required: false,
    value: ExperimentParameterRanges.INTEGRATION_TIME.default,
    options: [],
  });

  integrationTimeCField = new FormBase<number>({
    key: 'integrationTimeC',
    label: 'Integration Time C (ms)',
    type: 'number',
    placeholder: '',
    disabled: true,
    required: false,
    value: ExperimentParameterRanges.INTEGRATION_TIME.default,
    options: [],
  });

  integrationTimeDField = new FormBase<number>({
    key: 'integrationTimeD',
    label: 'Integration Time D (ms)',
    type: 'number',
    placeholder: '',
    disabled: true,
    required: false,
    value: ExperimentParameterRanges.INTEGRATION_TIME.default,
    options: [],
  });

  specGainAField = new FormBase<string>({
    key: 'specGainA',
    label: 'Gain A',
    type: 'radio',
    placeholder: '',
    disabled: true,
    required: false,
    value: 'lowA',
    options: [
      { key: 'lowA', value: 'Low' },
      { key: 'highA', value: 'High' },
    ],
  });

  specGainBField = new FormBase<string>({
    key: 'specGainB',
    label: 'Gain B',
    type: 'radio',
    placeholder: '',
    disabled: true,
    required: false,
    value: 'lowB',
    options: [
      { key: 'lowB', value: 'Low' },
      { key: 'highB', value: 'High' },
    ],
  });

  specGainCField = new FormBase<string>({
    key: 'specGainC',
    label: 'Gain C',
    type: 'radio',
    placeholder: '',
    disabled: true,
    required: false,
    value: 'lowC',
    options: [
      { key: 'lowC', value: 'Low' },
      { key: 'highC', value: 'High' },
    ],
  });

  specGainDField = new FormBase<string>({
    key: 'specGainD',
    label: 'Gain D',
    type: 'radio',
    placeholder: '',
    disabled: true,
    required: false,
    value: 'lowD',
    options: [
      { key: 'lowD', value: 'Low' },
      { key: 'highD', value: 'High' },
    ],
  });

  // Notifications
  enableNotificationsField = new FormBase<boolean>({
    key: 'enableNotifications',
    label: 'Enable Notifications',
    type: 'checkbox',
    placeholder: '',
    disabled: false,
    required: true,
    value: false,
    options: [],
  });

  notificationEmailField = new FormBase<string>({
    key: 'notificationEmail',
    label: 'Notification Email Recipient',
    type: 'dropdown',
    placeholder: 'Select email...',
    disabled: false,
    required: false,
    value: '',
    options: [],
  });

  fluorescenceChannelsField = new FormBase({
    key: 'fluorescenceChannels',
    label: 'Channels',
    type: 'dropdown',
    placeholder: 'Select 0-4 channels...',
    disabled: true,
    required: false,
    value: null,
    options: [],
  });

  // Oxygen
  salinityAField = new FormBase<number>({
    key: 'salinityA',
    label: 'Salinity A (g/l)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.SALINITY.default,
    options: [],
  });

  salinityBField = new FormBase<number>({
    key: 'salinityB',
    label: 'Salinity B (g/l)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.SALINITY.default,
    options: [],
  });

  salinityCField = new FormBase<number>({
    key: 'salinityC',
    label: 'Salinity C (g/l)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.SALINITY.default,
    options: [],
  });

  salinityDField = new FormBase<number>({
    key: 'salinityD',
    label: 'Salinity D (g/l)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.SALINITY.default,
    options: [],
  });

  spargingControlTypeAField = new FormBase<string>({
    key: 'spargingControlTypeA',
    label: 'Control Type A',
    type: 'radio',
    placeholder: '',
    disabled: false,
    required: false,
    value: 'FixedA',
    options: [
      { key: 'FixedA', value: 'Fixed' },
      { key: 'DisabledA', value: 'Disabled' },
    ],
  });

  spargingControlTypeBField = new FormBase<string>({
    key: 'spargingControlTypeB',
    label: 'Control Type B',
    type: 'radio',
    placeholder: '',
    disabled: false,
    required: false,
    value: 'FixedB',
    options: [
      { key: 'FixedB', value: 'Fixed' },
      { key: 'DisabledB', value: 'Disabled' },
    ],
  });

  spargingControlTypeCField = new FormBase<string>({
    key: 'spargingControlTypeC',
    label: 'Control Type C',
    type: 'radio',
    placeholder: '',
    disabled: false,
    required: false,
    value: 'FixedC',
    options: [
      { key: 'FixedC', value: 'Fixed' },
      { key: 'DisabledC', value: 'Disabled' },
    ],
  });

  spargingControlTypeDField = new FormBase<string>({
    key: 'spargingControlTypeD',
    label: 'Control Type D',
    type: 'radio',
    placeholder: '',
    disabled: false,
    required: false,
    value: 'FixedD',
    options: [
      { key: 'FixedD', value: 'Fixed' },
      { key: 'DisabledD', value: 'Disabled' },
    ],
  });

  airflowAField = new FormBase<number>({
    key: 'airflowA',
    label: 'Airflow Rate A',
    type: 'range',
    placeholder: '',
    disabled: true,
    required: false,
    value: ExperimentParameterRanges.SPARGING_AIRFLOW.default,
    options: [],
  });

  airflowBField = new FormBase<number>({
    key: 'airflowB',
    label: 'Airflow Rate B',
    type: 'range',
    placeholder: '',
    disabled: true,
    required: false,
    value: ExperimentParameterRanges.SPARGING_AIRFLOW.default,
    options: [],
  });

  airflowCField = new FormBase<number>({
    key: 'airflowC',
    label: 'Airflow Rate C',
    type: 'range',
    placeholder: '',
    disabled: true,
    required: false,
    value: ExperimentParameterRanges.SPARGING_AIRFLOW.default,
    options: [],
  });

  airflowDField = new FormBase<number>({
    key: 'airflowD',
    label: 'Airflow Rate D',
    type: 'range',
    placeholder: '',
    disabled: true,
    required: false,
    value: ExperimentParameterRanges.SPARGING_AIRFLOW.default,
    options: [],
  });

  morbidostatAActiveField = new FormBase<boolean>({
    key: 'morbidostatAActive',
    label: 'Morbidostat A',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: true,
    options: [],
  });

  morbidostatBActiveField = new FormBase<boolean>({
    key: 'morbidostatBActive',
    label: 'Morbidostat B',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: true,
    options: [],
  });

  morbidostatCActiveField = new FormBase<boolean>({
    key: 'morbidostatCActive',
    label: 'Morbidostat C',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: true,
    options: [],
  });

  morbidostatDActiveField = new FormBase<boolean>({
    key: 'morbidostatDActive',
    label: 'Morbidostat D',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: true,
    options: [],
  });

  thresholdODAField = new FormBase<number>({
    key: 'thresholdODA',
    label: 'Threshold A',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.OD_THRESHOLD.default,
    options: [],
  });

  thresholdODBField = new FormBase<number>({
    key: 'thresholdODB',
    label: 'Threshold B',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.OD_THRESHOLD.default,
    options: [],
  });

  thresholdODCField = new FormBase<number>({
    key: 'thresholdODC',
    label: 'Threshold C',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.OD_THRESHOLD.default,
    options: [],
  });

  thresholdODDField = new FormBase<number>({
    key: 'thresholdODD',
    label: 'Threshold D',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.OD_THRESHOLD.default,
    options: [],
  });

  dilutionPeriodAField = new FormBase<number>({
    key: 'dilutionPeriodA',
    label: 'Dilution Period A (mins)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.DILUTION_PERIOD_MINS.default,
    options: [],
  });

  dilutionPeriodBField = new FormBase<number>({
    key: 'dilutionPeriodB',
    label: 'Dilution Period B (mins)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.DILUTION_PERIOD_MINS.default,
    options: [],
  });

  dilutionPeriodCField = new FormBase<number>({
    key: 'dilutionPeriodC',
    label: 'Dilution Period C (mins)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.DILUTION_PERIOD_MINS.default,
    options: [],
  });

  dilutionPeriodDField = new FormBase<number>({
    key: 'dilutionPeriodD',
    label: 'Dilution Period D (mins)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.DILUTION_PERIOD_MINS.default,
    options: [],
  });

  dilutionFactorAField = new FormBase<number>({
    key: 'dilutionFactorA',
    label: 'Dilution Factor A',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.DILUTION_FACTOR.default,
    options: [],
  });

  dilutionFactorBField = new FormBase<number>({
    key: 'dilutionFactorB',
    label: 'Dilution Factor B',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.DILUTION_FACTOR.default,
    options: [],
  });

  dilutionFactorCField = new FormBase<number>({
    key: 'dilutionFactorC',
    label: 'Dilution Factor C',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.DILUTION_FACTOR.default,
    options: [],
  });

  dilutionFactorDField = new FormBase<number>({
    key: 'dilutionFactorD',
    label: 'Dilution Factor D',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: ExperimentParameterRanges.DILUTION_FACTOR.default,
    options: [],
  });

  // ── Shared forms ───────────────────────────────────────────────────────────

  experimentControlsForm = this.formBuilder.group({
    // General experiment controls
    [this.experimentNameField.key]: [
      '',
      [
        Validators.minLength(2),
        Validators.maxLength(100),
        Validators.required,
        Validators.pattern(advancedNameRegex),
      ],
    ],
    [this.organismField.key]: [
      '',
      [Validators.minLength(2), Validators.maxLength(100), Validators.required],
    ],
    [this.strainField.key]: [
      '',
      [Validators.minLength(2), Validators.maxLength(100), Validators.required],
    ],
    [this.mediaField.key]: [
      '',
      [Validators.minLength(2), Validators.maxLength(100), Validators.required],
    ],
    [this.samplingTimeField.key]: [
      this.samplingTimeField.value,
      Validators.compose([
        Validators.min(this.minSampleTimeInMins),
        Validators.max(this.maxSampleTimeInMins),
        Validators.required,
        stepValidator(this.sampleTimeStep),
      ]),
    ],
    [this.experimentTimeField.key]: [
      this.experimentTimeField.value,
      Validators.compose([
        Validators.min(this.minExperimentTimeInHours),
        Validators.max(this.maxExperimentTimeInHours),
        Validators.required,
        stepValidator(this.experimentTimeStep),
      ]),
    ],
    [this.speedAField.key]: [
      this.speedAField.value,
      Validators.compose([
        Validators.min(this.minSpeed),
        Validators.max(this.maxSpeed),
        stepValidator(this.speedStep),
      ]),
    ],
    [this.speedBField.key]: [
      this.speedBField.value,
      Validators.compose([
        Validators.min(this.minSpeed),
        Validators.max(this.maxSpeed),
        stepValidator(this.speedStep),
      ]),
    ],
    [this.speedCField.key]: [
      this.speedCField.value,
      Validators.compose([
        Validators.min(this.minSpeed),
        Validators.max(this.maxSpeed),
        stepValidator(this.speedStep),
      ]),
    ],
    [this.speedDField.key]: [
      this.speedDField.value,
      Validators.compose([
        Validators.min(this.minSpeed),
        Validators.max(this.maxSpeed),
        stepValidator(this.speedStep),
      ]),
    ],
    [this.setStirringSpeedAField.key]: [this.setStirringSpeedAField.value],
    [this.setStirringSpeedBField.key]: [this.setStirringSpeedBField.value],
    [this.setStirringSpeedCField.key]: [this.setStirringSpeedCField.value],
    [this.setStirringSpeedDField.key]: [this.setStirringSpeedDField.value],
    [this.ambientTemperatureField.key]: [
      this.ambientTemperatureField.value,
      [
        Validators.min(this.minAmbientTemp),
        Validators.max(this.maxAmbientTemp),
        stepValidator(this.ambientTempStep),
      ],
    ],
    [this.temperatureControlAField.key]: [this.temperatureControlAField.value],
    [this.temperatureControlBField.key]: [this.temperatureControlBField.value],
    [this.temperatureControlCField.key]: [this.temperatureControlCField.value],
    [this.temperatureControlDField.key]: [this.temperatureControlDField.value],
    [this.temperatureAField.key]: [
      this.temperatureAField.value,
      [
        Validators.min(this.minCultureTemp),
        Validators.max(this.maxAmbientTemp),
        stepValidator(this.cultureTempStep),
      ],
    ],
    [this.temperatureBField.key]: [
      this.temperatureBField.value,
      [
        Validators.min(this.minCultureTemp),
        Validators.max(this.maxAmbientTemp),
        stepValidator(this.cultureTempStep),
      ],
    ],
    [this.temperatureCField.key]: [
      this.temperatureCField.value,
      [
        Validators.min(this.minCultureTemp),
        Validators.max(this.maxAmbientTemp),
        stepValidator(this.cultureTempStep),
      ],
    ],
    [this.temperatureDField.key]: [
      this.temperatureDField.value,
      [
        Validators.min(this.minCultureTemp),
        Validators.max(this.maxAmbientTemp),
        stepValidator(this.cultureTempStep),
      ],
    ],
    [this.maintainODField.key]: [this.maintainODField.value],
    // Turbidostat controls
    [this.targetODAField.key]: [
      this.targetODAField.value,
      [
        Validators.min(this.minTargetOD),
        Validators.max(this.maxTargetOD),
        stepValidator(this.targetODStep),
      ],
    ],
    [this.targetODBField.key]: [
      this.targetODBField.value,
      [
        Validators.min(this.minTargetOD),
        Validators.max(this.maxTargetOD),
        stepValidator(this.targetODStep),
      ],
    ],
    [this.targetODCField.key]: [
      this.targetODCField.value,
      [
        Validators.min(this.minTargetOD),
        Validators.max(this.maxTargetOD),
        stepValidator(this.targetODStep),
      ],
    ],
    [this.targetODDField.key]: [
      this.targetODDField.value,
      [
        Validators.min(this.minTargetOD),
        Validators.max(this.maxTargetOD),
        stepValidator(this.targetODStep),
      ],
    ],
    [this.turbAActiveField.key]: [this.turbAActiveField.value],
    [this.turbBActiveField.key]: [this.turbBActiveField.value],
    [this.turbCActiveField.key]: [this.turbCActiveField.value],
    [this.turbDActiveField.key]: [this.turbDActiveField.value],
    // Chemostat controls
    [this.chemoAActiveField.key]: [this.chemoAActiveField.value],
    [this.chemoBActiveField.key]: [this.chemoBActiveField.value],
    [this.chemoCActiveField.key]: [this.chemoCActiveField.value],
    [this.chemoDActiveField.key]: [this.chemoDActiveField.value],
    [this.flowRateAField.key]: [
      this.flowRateAField.value,
      Validators.compose([
        Validators.min(this.minFlowRate),
        Validators.max(this.maxFlowRate),
        stepValidator(this.flowRateStep),
      ]),
    ],
    [this.flowRateBField.key]: [
      this.flowRateBField.value,
      Validators.compose([
        Validators.min(this.minFlowRate),
        Validators.max(this.maxFlowRate),
        stepValidator(this.flowRateStep),
      ]),
    ],
    [this.flowRateCField.key]: [
      this.flowRateCField.value,
      Validators.compose([
        Validators.min(this.minFlowRate),
        Validators.max(this.maxFlowRate),
        stepValidator(this.flowRateStep),
      ]),
    ],
    [this.flowRateDField.key]: [
      this.flowRateDField.value,
      Validators.compose([
        Validators.min(this.minFlowRate),
        Validators.max(this.maxFlowRate),
        stepValidator(this.flowRateStep),
      ]),
    ],
    // pH controls
    [this.phControlAField.key]: [this.phControlAField.value],
    [this.phControlBField.key]: [this.phControlBField.value],
    [this.phControlCField.key]: [this.phControlCField.value],
    [this.phControlDField.key]: [this.phControlDField.value],
    [this.targetPHAField.key]: [
      this.targetPHAField.value,
      Validators.compose([
        Validators.min(this.minTargetPH),
        Validators.max(this.maxTargetPH),
        stepValidator(this.targetPHStep),
      ]),
    ],
    [this.targetPHBField.key]: [
      this.targetPHBField.value,
      Validators.compose([
        Validators.min(this.minTargetPH),
        Validators.max(this.maxTargetPH),
        stepValidator(this.targetPHStep),
      ]),
    ],
    [this.targetPHCField.key]: [
      this.targetPHCField.value,
      Validators.compose([
        Validators.min(this.minTargetPH),
        Validators.max(this.maxTargetPH),
        stepValidator(this.targetPHStep),
      ]),
    ],
    [this.targetPHDField.key]: [
      this.targetPHDField.value,
      Validators.compose([
        Validators.min(this.minTargetPH),
        Validators.max(this.maxTargetPH),
        stepValidator(this.targetPHStep),
      ]),
    ],
    [this.inputTypeAField.key]: [this.inputTypeAField.value],
    [this.inputTypeBField.key]: [this.inputTypeBField.value],
    [this.inputTypeCField.key]: [this.inputTypeCField.value],
    [this.inputTypeDField.key]: [this.inputTypeDField.value],
    [this.pumpDurationAField.key]: [
      this.pumpDurationAField.value,
      Validators.compose([
        Validators.min(this.minPumpDuration),
        Validators.max(this.maxPumpDuration),
        stepValidator(this.pumpDurationStep),
      ]),
    ],
    [this.pumpDurationBField.key]: [
      this.pumpDurationBField.value,
      Validators.compose([
        Validators.min(this.minPumpDuration),
        Validators.max(this.maxPumpDuration),
        stepValidator(this.pumpDurationStep),
      ]),
    ],
    [this.pumpDurationCField.key]: [
      this.pumpDurationCField.value,
      Validators.compose([
        Validators.min(this.minPumpDuration),
        Validators.max(this.maxPumpDuration),
        stepValidator(this.pumpDurationStep),
      ]),
    ],
    [this.pumpDurationDField.key]: [
      this.pumpDurationDField.value,
      Validators.compose([
        Validators.min(this.minPumpDuration),
        Validators.max(this.maxPumpDuration),
        stepValidator(this.pumpDurationStep),
      ]),
    ],
    // pH 2x controls
    [this.ph2ControlAField.key]: [this.ph2ControlAField.value],
    [this.ph2ControlBField.key]: [this.ph2ControlBField.value],
    [this.targetPH2AField.key]: [
      this.targetPH2AField.value,
      Validators.compose([
        Validators.min(this.minTargetPH),
        Validators.max(this.maxTargetPH),
        stepValidator(this.targetPHStep),
      ]),
    ],
    [this.targetPH2BField.key]: [
      this.targetPH2BField.value,
      Validators.compose([
        Validators.min(this.minTargetPH),
        Validators.max(this.maxTargetPH),
        stepValidator(this.targetPHStep),
      ]),
    ],
    [this.pumpDuration2AField.key]: [
      this.pumpDuration2AField.value,
      Validators.compose([
        Validators.min(this.minPumpDuration),
        Validators.max(this.maxPumpDuration),
        stepValidator(this.pumpDurationStep),
      ]),
    ],
    [this.pumpDuration2BField.key]: [
      this.pumpDuration2BField.value,
      Validators.compose([
        Validators.min(this.minPumpDuration),
        Validators.max(this.maxPumpDuration),
        stepValidator(this.pumpDurationStep),
      ]),
    ],
    // pH + Chemostat controls
    [this.phChemoControlAField.key]: [this.phChemoControlAField.value],
    [this.phChemoControlBField.key]: [this.phChemoControlBField.value],
    [this.targetPHChemoAField.key]: [
      this.targetPHChemoAField.value,
      Validators.compose([
        Validators.min(this.minTargetPH),
        Validators.max(this.maxTargetPH),
        stepValidator(this.targetPHStep),
      ]),
    ],
    [this.targetPHChemoBField.key]: [
      this.targetPHChemoBField.value,
      Validators.compose([
        Validators.min(this.minTargetPH),
        Validators.max(this.maxTargetPH),
        stepValidator(this.targetPHStep),
      ]),
    ],
    [this.pumpDurationChemoAField.key]: [
      this.pumpDurationChemoAField.value,
      Validators.compose([
        Validators.min(this.minPumpDuration),
        Validators.max(this.maxPumpDuration),
        stepValidator(this.pumpDurationStep),
      ]),
    ],
    [this.pumpDurationChemoBField.key]: [
      this.pumpDurationChemoBField.value,
      Validators.compose([
        Validators.min(this.minPumpDuration),
        Validators.max(this.maxPumpDuration),
        stepValidator(this.pumpDurationStep),
      ]),
    ],
    [this.inputTypeChemoAField.key]: [this.inputTypeChemoAField.value],
    [this.inputTypeChemoBField.key]: [this.inputTypeChemoBField.value],
    [this.flowRatePHAField.key]: [
      this.flowRatePHAField.value,
      Validators.compose([
        Validators.min(this.minFlowRate),
        Validators.max(this.maxFlowRate),
        stepValidator(this.flowRateStep),
      ]),
    ],
    [this.flowRatePHBField.key]: [
      this.flowRatePHBField.value,
      Validators.compose([
        Validators.min(this.minFlowRate),
        Validators.max(this.maxFlowRate),
        stepValidator(this.flowRateStep),
      ]),
    ],
    // Fluoro controls
    [this.led1OnField.key]: [this.led1OnField.value],
    [this.led2OnField.key]: [this.led2OnField.value],
    [this.led3OnField.key]: [this.led3OnField.value],
    [this.led1IntensityField.key]: [
      this.led1IntensityField.value,
      Validators.compose([
        Validators.min(this.minLEDIntensity),
        Validators.max(this.maxLEDIntensity),
        stepValidator(this.ledIntensityStep),
      ]),
    ],
    [this.led2IntensityField.key]: [
      this.led2IntensityField.value,
      Validators.compose([
        Validators.min(this.minLEDIntensity),
        Validators.max(this.maxLEDIntensity),
        stepValidator(this.ledIntensityStep),
      ]),
    ],
    [this.led3IntensityField.key]: [
      this.led3IntensityField.value,
      Validators.compose([
        Validators.min(this.minLEDIntensity),
        Validators.max(this.maxLEDIntensity),
        stepValidator(this.ledIntensityStep),
      ]),
    ],
    [this.integrationTimeAField.key]: [
      this.integrationTimeAField.value,
      Validators.compose([
        Validators.min(this.minIntegrationTime),
        Validators.max(this.maxIntegrationTime),
        stepValidator(this.integrationTimeStep),
      ]),
    ],
    [this.integrationTimeBField.key]: [
      this.integrationTimeBField.value,
      Validators.compose([
        Validators.min(this.minIntegrationTime),
        Validators.max(this.maxIntegrationTime),
        stepValidator(this.integrationTimeStep),
      ]),
    ],
    [this.integrationTimeCField.key]: [
      this.integrationTimeCField.value,
      Validators.compose([
        Validators.min(this.minIntegrationTime),
        Validators.max(this.maxIntegrationTime),
        stepValidator(this.integrationTimeStep),
      ]),
    ],
    [this.integrationTimeDField.key]: [
      this.integrationTimeDField.value,
      Validators.compose([
        Validators.min(this.minIntegrationTime),
        Validators.max(this.maxIntegrationTime),
        stepValidator(this.integrationTimeStep),
      ]),
    ],
    [this.specGainAField.key]: [this.specGainAField.value],
    [this.specGainBField.key]: [this.specGainBField.value],
    [this.specGainCField.key]: [this.specGainCField.value],
    [this.specGainDField.key]: [this.specGainDField.value],
    [this.fluorescenceChannelsField.key]: [
      this.fluorescenceChannelsField.value,
    ],
    // Oxygen controls
    [this.salinityAField.key]: [
      this.salinityAField.value,
      Validators.compose([
        Validators.min(this.minSalinity),
        Validators.max(this.maxSalinity),
        stepValidator(this.salinityStep),
      ]),
    ],
    [this.salinityBField.key]: [
      this.salinityBField.value,
      Validators.compose([
        Validators.min(this.minSalinity),
        Validators.max(this.maxSalinity),
        stepValidator(this.salinityStep),
      ]),
    ],
    [this.salinityCField.key]: [
      this.salinityCField.value,
      Validators.compose([
        Validators.min(this.minSalinity),
        Validators.max(this.maxSalinity),
        stepValidator(this.salinityStep),
      ]),
    ],
    [this.salinityDField.key]: [
      this.salinityDField.value,
      Validators.compose([
        Validators.min(this.minSalinity),
        Validators.max(this.maxSalinity),
        stepValidator(this.salinityStep),
      ]),
    ],
    [this.spargingControlTypeAField.key]: [
      this.spargingControlTypeAField.value,
      [],
    ],
    [this.spargingControlTypeBField.key]: [
      this.spargingControlTypeBField.value,
      [],
    ],
    [this.spargingControlTypeCField.key]: [
      this.spargingControlTypeCField.value,
      [],
    ],
    [this.spargingControlTypeDField.key]: [
      this.spargingControlTypeDField.value,
      [],
    ],
    [this.airflowAField.key]: [
      { value: this.airflowAField.value, disabled: this.airflowAField.disabled },
      Validators.compose([Validators.min(this.minAirflow), Validators.max(this.maxAirflow)]),
    ],
    [this.airflowBField.key]: [
      { value: this.airflowBField.value, disabled: this.airflowBField.disabled },
      Validators.compose([Validators.min(this.minAirflow), Validators.max(this.maxAirflow)]),
    ],
    [this.airflowCField.key]: [
      { value: this.airflowCField.value, disabled: this.airflowCField.disabled },
      Validators.compose([Validators.min(this.minAirflow), Validators.max(this.maxAirflow)]),
    ],
    [this.airflowDField.key]: [
      { value: this.airflowDField.value, disabled: this.airflowDField.disabled },
      Validators.compose([Validators.min(this.minAirflow), Validators.max(this.maxAirflow)]),
    ],
    [this.morbidostatAActiveField.key]: [this.morbidostatAActiveField.value],
    [this.morbidostatBActiveField.key]: [this.morbidostatBActiveField.value],
    [this.morbidostatCActiveField.key]: [this.morbidostatCActiveField.value],
    [this.morbidostatDActiveField.key]: [this.morbidostatDActiveField.value],
    [this.thresholdODAField.key]: [
      this.thresholdODAField.value,
      Validators.compose([
        Validators.min(this.minThreshold),
        Validators.max(this.maxThreshold),
        stepValidator(this.thresholdStep),
      ]),
    ],
    [this.thresholdODBField.key]: [
      this.thresholdODBField.value,
      Validators.compose([
        Validators.min(this.minThreshold),
        Validators.max(this.maxThreshold),
        stepValidator(this.thresholdStep),
      ]),
    ],
    [this.thresholdODCField.key]: [
      this.thresholdODCField.value,
      Validators.compose([
        Validators.min(this.minThreshold),
        Validators.max(this.maxThreshold),
        stepValidator(this.thresholdStep),
      ]),
    ],
    [this.thresholdODDField.key]: [
      this.thresholdODDField.value,
      Validators.compose([
        Validators.min(this.minThreshold),
        Validators.max(this.maxThreshold),
        stepValidator(this.thresholdStep),
      ]),
    ],
    [this.dilutionPeriodAField.key]: [
      this.dilutionPeriodAField.value,
      Validators.compose([
        Validators.min(this.minDilutionPeriod),
        Validators.max(this.maxDilutionPeriod),
        stepValidator(this.dilutionPeriodStep),
      ]),
    ],
    [this.dilutionPeriodBField.key]: [
      this.dilutionPeriodBField.value,
      Validators.compose([
        Validators.min(this.minDilutionPeriod),
        Validators.max(this.maxDilutionPeriod),
        stepValidator(this.dilutionPeriodStep),
      ]),
    ],
    [this.dilutionPeriodCField.key]: [
      this.dilutionPeriodCField.value,
      Validators.compose([
        Validators.min(this.minDilutionPeriod),
        Validators.max(this.maxDilutionPeriod),
        stepValidator(this.dilutionPeriodStep),
      ]),
    ],
    [this.dilutionPeriodDField.key]: [
      this.dilutionPeriodDField.value,
      Validators.compose([
        Validators.min(this.minDilutionPeriod),
        Validators.max(this.maxDilutionPeriod),
        stepValidator(this.dilutionPeriodStep),
      ]),
    ],
    [this.dilutionFactorAField.key]: [
      this.dilutionFactorAField.value,
      Validators.compose([
        Validators.min(this.minDilutionFactor),
        Validators.max(this.maxDilutionFactor),
        stepValidator(this.dilutionFactorStep),
      ]),
    ],
    [this.dilutionFactorBField.key]: [
      this.dilutionFactorBField.value,
      Validators.compose([
        Validators.min(this.minDilutionFactor),
        Validators.max(this.maxDilutionFactor),
        stepValidator(this.dilutionFactorStep),
      ]),
    ],
    [this.dilutionFactorCField.key]: [
      this.dilutionFactorCField.value,
      Validators.compose([
        Validators.min(this.minDilutionFactor),
        Validators.max(this.maxDilutionFactor),
        stepValidator(this.dilutionFactorStep),
      ]),
    ],
    [this.dilutionFactorDField.key]: [
      this.dilutionFactorDField.value,
      Validators.compose([
        Validators.min(this.minDilutionFactor),
        Validators.max(this.maxDilutionFactor),
        stepValidator(this.dilutionFactorStep),
      ]),
    ],
    [this.experimentModeField.key]: [this.experimentModeField.value, []],
    // Notifications
    [this.enableNotificationsField.key]: [
      this.enableNotificationsField.value,
      [Validators.required],
    ],
    [this.notificationEmailField.key]: [this.notificationEmailField.value, []],
  });

  moduleSelectForm = this.formBuilder.group({
    [this.moduleCheckboxesField.key]: new FormArray(
      this.moduleCheckboxesField.options.map(() =>
        this.formBuilder.control(false),
      ),
    ),
  });

  // Pagination form controls
  changeLogsPerPageField = new FormBase({
    key: 'changeLogsPerPage',
    label: 'Page size',
    type: 'dropdown',
    placeholder: '',
    disabled: false,
    required: false,
    value: '',
    options: [
      { key: '5', value: '5' },
      { key: '10', value: '10' },
      { key: '25', value: '25' },
      { key: '50', value: '50' },
      { key: '100', value: '100' },
    ],
  });

  paginationControls = this.formBuilder.group({
    [this.changeLogsPerPageField.key]: [this.pageSize().toString()],
  });

  protected readonly unsubscribe$ = new Subject<void>();

  protected subscribeNotificationEmail(): void {
    this.f[this.notificationEmailField.key]?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((email: string) => {
        this.notificationEmailValue.set(email ?? '');
      });
  }

  // ── Accessors ──────────────────────────────────────────────────────────────

  get f(): FormGroup['controls'] {
    return this.experimentControlsForm.controls;
  }

  get checkboxesFormArray(): FormArray {
    return this.moduleSelectForm.get('moduleCheckboxes') as FormArray;
  }

  // ── Shared concrete methods ────────────────────────────────────────────────

  getPrevious(): void {
    this.pageRequested.set(this.currentPage() - 1);
    this.fetchChangelog();
  }

  getNext(): void {
    this.pageRequested.set(this.currentPage() + 1);
    this.fetchChangelog();
  }

  getClasses(sortTerm: string): string[] {
    const active = this.currentSort().sortBy === sortTerm ? 'active' : '';
    return [active];
  }

  onSort(sortTerm: string): void {
    if (this.currentSort().sortBy === sortTerm) {
      this.currentSort.update((sort) => ({
        ...sort,
        sortDirection: sort.sortDirection === 'asc' ? 'desc' : 'asc',
      }));
    } else {
      this.currentSort.set({ sortBy: sortTerm, sortDirection: 'asc' });
    }
    this.fetchChangelog();
  }

  getFluorescenceChannels(): void {
    // Set loading to true and clear previous errors
    this.fluorescenceChannelsLoading.set(true);

    // Clear previous fluorescence channel list and dropdown options before fetching channels again
    this.fluorescenceChannels.set([]);
    this.fluorescenceChannelsField.options = [];

    this.deviceService.getFluorescenceChannels().subscribe({
      next: (response: FluorescenceChannel[]) => {
        this.fluorescenceChannelsError.set(null);
        this.fluorescenceChannels.set(response);
        // Populate device dropdown options, skipping any channels already selected
        const selectedSlots = new Set<number>(
          this.selectedFluorescenceChannels().map((c) => c.memorySlot),
        );
        response.forEach((channel: FluorescenceChannel) => {
          if (!selectedSlots.has(channel.memorySlot)) {
            this.fluorescenceChannelsField.options.push({
              key: channel.memorySlot,
              value: channel.channelName,
            });
          }
        });
        this.fluorescenceChannelsLoading.set(false);
      },
      error: (error: ErrorObject) => {
        this.fluorescenceChannelsLoading.set(false);
        this.fluorescenceChannelsError.set(error);
      },
    });
  }

  /**
   * @remarks
   * Show warning if ambient temperature is below 15°C or above 40°C
   * @returns boolean - true if ambient temperature is below 15°C or above 40°C
   */
  isAmbientTemperatureInWarningRange(): boolean {
    return (
      this.f[this.ambientTemperatureField.key].value <
        this.minAmbientTempWarning ||
      this.f[this.ambientTemperatureField.key].value >
        this.maxAmbientTempWarning
    );
  }

  getAmbientTemperatureInWarningRange(): string {
    if (
      this.f[this.ambientTemperatureField.key].value <
        this.minAmbientTempWarning ||
      this.f[this.ambientTemperatureField.key].value >
        this.maxAmbientTempWarning
    ) {
      return ExperimentParameterRanges.AMBIENT_TEMPERATURE.warningMessage;
    }
    return '';
  }

  initialiseCheckboxes(): void {
    // Reset form array
    clearFormArray(this.checkboxesFormArray);
    this.moduleCheckboxesField.options.forEach(() => {
      const control = this.formBuilder.control(false);
      this.checkboxesFormArray.push(control);
    });
  }

  removeFluorescenceChannel(channelId: number): void {
    const channel = this.selectedFluorescenceChannels().find(
      (c: FluorescenceChannel) => c.memorySlot === channelId,
    );
    if (!channel) {
      return;
    }
    this.selectedFluorescenceChannels.update((channels: FluorescenceChannel[]) =>
      channels.filter((c: FluorescenceChannel) => c.memorySlot !== channelId),
    );
    this.fluorescenceChannelsField.options.push({
      key: channel.memorySlot,
      value: channel.channelName,
    });
    this.fluorescenceChannelsField.options.sort((a, b) =>
      a.value.localeCompare(b.value),
    );
    this.experimentControlsForm
      .get(this.fluorescenceChannelsField.key)
      .setValue(null);
    this.experimentControlsForm.markAsDirty();
    if (this.selectedFluorescenceChannels().length < 4) {
      this.experimentControlsForm
        .get(this.fluorescenceChannelsField.key)
        .enable();
    }
  }

  resetCheckboxes(): void {
    const resetValues = this.checkboxesFormArray.controls.map(() => false);
    this.checkboxesFormArray.patchValue(resetValues);
  }

  updateIdInUrl(id: number): void {
    window.history.replaceState({}, '', `/experiment/${id}/set-parameters`);
  }

  openModal(modalId: string): void {
    this.modalService.open(modalId);
  }

  closeModal(modalId: string): void {
    this.modalService.close(modalId);
  }

  filterFluorescenceChannelOptions(): void {
    this.selectedFluorescenceChannels().forEach((channel: FluorescenceChannel) => {
      this.fluorescenceChannelsField.options =
        this.fluorescenceChannelsField.options.filter(
          (option) => option.key !== channel.memorySlot,
        );
    });
  }

  handleExperimentModeChange(value: number): void {
    this.updateRequiredModules(value as ExperimentMode);

    // If we have an existing experiment, update form values but preserve the newly selected mode
    if (this.getSavedEntity()) {
      // Update form values but preserve the current mode selection
      const formValues = {
        ...this.getBasicFormValues(this.getSavedEntity(), true), // preserve current mode
        ...this.getDefaultModuleFormValues(this.getSavedEntity()),
        ...this.getTurbidostatFormValues(this.getSavedEntity()),
        ...this.getChemostatFormValues(this.getSavedEntity()),
        ...this.getPHControlFormValues(this.getSavedEntity()),
        ...this.getFluorescenceFormValues(this.getSavedEntity()),
        ...this.getOxygenFormValues(this.getSavedEntity()),
        ...this.getSpargingFormValues(this.getSavedEntity()),
        ...this.getMorbidostatFormValues(this.getSavedEntity()),
      };

      this.experimentControlsForm.patchValue(formValues);
    }

    this.onAfterExperimentModeChange();
    this.experimentControlsForm.markAsDirty();
  }

  moduleCheckboxClicked(event: Event): void {
    const target = event.target as HTMLFormElement;
    this.updateModuleFromCheckbox(target.name, target.checked);

    this.onAfterModuleCheckboxClicked();
    this.moduleSelectForm.markAsDirty();
  }

  resetForm(): void {
    // If experiment ID exists and is saved, reset form values to original experiment value
    // else reset form & set default values
    this.experimentControlsForm.reset();

    if (this.entityId() && this.getSavedEntity()) {
      // Reset module flags to match existing experiment
      this.useFluorescence.set(this.getSavedEntity().useFluorescence);
      this.useOxygen.set(this.getSavedEntity().useOxygen);
      this.usePH.set(this.getSavedEntity().usePH);
      this.useSparging.set(this.getSavedEntity().useSparging);

      // Use helper methods to get form values for existing experiment
      const formValues = {
        ...this.getBasicFormValues({
          ...this.getSavedEntity(),
        }),
        ...this.getDefaultModuleFormValues(this.getSavedEntity()),
        ...this.getTurbidostatFormValues(this.getSavedEntity()),
        ...this.getChemostatFormValues(this.getSavedEntity()),
        ...this.getPHControlFormValues(this.getSavedEntity()),
        ...this.getFluorescenceFormValues(this.getSavedEntity()),
        ...this.getOxygenFormValues(this.getSavedEntity()),
        ...this.getSpargingFormValues(this.getSavedEntity()),
        ...this.getMorbidostatFormValues(this.getSavedEntity()),
      };
      this.experimentControlsForm.patchValue(formValues);

      // Set all module field states based on existing experiment after patching values
      this.setFluorescenceFieldsState(this.getSavedEntity().useFluorescence);
    } else {
      // Reset module flags to default values for new experiment
      this.useFluorescence.set(false);
      this.useOxygen.set(false);
      this.usePH.set(false);
      this.useSparging.set(false);

      // Reset required modules array
      this.requiredDeviceModules.set([]);

      // Use default values for new experiment (when experimentId is 0 or null)
      this.experimentControlsForm.patchValue(this.getDefaultFormValues());

      // Explicitly disable fluorescence fields since they weren't included in patchValue
      this.setFluorescenceFieldsState(false);
    }
    this.resetCheckboxes();
    this.experimentControlsForm.markAsPristine();
  }

  protected isNavigationAllowed(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if (
        this.experimentControlsForm.pristine ||
        !this.currentUser() ||
        this.authEventService.isUnauthorizedNavigationPending
      ) {
        resolve(true);
      } else {
        this.openModal('attention');
        this.allowNavigation
          .pipe(take(1))
          .subscribe((isConfirmed: boolean) => resolve(isConfirmed));
      }
    });
  }

  // ── Copy methods ───────────────────────────────────────────────────────────

  copyChemostatControlValues(): void {
    const chemoActiveValueA = this.f[this.chemoAActiveField.key]
      .value as boolean;
    const flowRateValueA = this.f[this.flowRateAField.key].value as number;
    const targets: [FormBase<boolean>, FormBase<number>][] = [
      [this.chemoBActiveField, this.flowRateBField],
      [this.chemoCActiveField, this.flowRateCField],
      [this.chemoDActiveField, this.flowRateDField],
    ];
    targets.forEach(([activeField, flowRateField]) => {
      this.experimentControlsForm.controls[activeField.key].setValue(
        chemoActiveValueA,
      );
      this.experimentControlsForm.controls[flowRateField.key].setValue(
        flowRateValueA,
      );
    });
    this.experimentControlsForm.markAsDirty();
  }

  copyIntegrationTimeValues(): void {
    const integrationTimeValueA = this.f[this.integrationTimeAField.key]
      .value as number;
    const specGainValueA = this.f[this.specGainAField.key].value as
      | string
      | null;
    const specGainBaseValueA = specGainValueA?.slice(0, -1) ?? null;
    const targets: [FormBase<number>, FormBase<string>, string][] = [
      [this.integrationTimeBField, this.specGainBField, 'B'],
      [this.integrationTimeCField, this.specGainCField, 'C'],
      [this.integrationTimeDField, this.specGainDField, 'D'],
    ];
    targets.forEach(([integrationField, specGainField, suffix]) => {
      this.experimentControlsForm.controls[integrationField.key].setValue(
        integrationTimeValueA,
      );
      if (specGainBaseValueA !== null) {
        this.experimentControlsForm.controls[specGainField.key].setValue(
          `${specGainBaseValueA}${suffix}`,
        );
      }
    });
    this.experimentControlsForm.markAsDirty();
  }

  copyLEDValues(): void {
    const led1OnValue = this.f[this.led1OnField.key].value as boolean;
    const led1IntensityValue = this.f[this.led1IntensityField.key]
      .value as number;
    const integrationTimeValueA = this.f[this.integrationTimeAField.key]
      .value as number;
    const specGainValueA = this.f[this.specGainAField.key].value as
      | string
      | null;
    const specGainBaseValueA = specGainValueA?.slice(0, -1) ?? null;

    // Copy LED 1 → 2, 3
    (
      [
        [this.led2OnField, this.led2IntensityField],
        [this.led3OnField, this.led3IntensityField],
      ] as [FormBase<boolean>, FormBase<number>][]
    ).forEach(([onField, intensityField]) => {
      this.experimentControlsForm.controls[onField.key].setValue(led1OnValue);
      this.experimentControlsForm.controls[intensityField.key].setValue(
        led1IntensityValue,
      );
    });

    // Copy integration time and specGain A → B, C
    (
      [
        [this.integrationTimeBField, this.specGainBField, 'B'],
        [this.integrationTimeCField, this.specGainCField, 'C'],
      ] as [FormBase<number>, FormBase<string>, string][]
    ).forEach(([integrationField, specGainField, suffix]) => {
      this.experimentControlsForm.controls[integrationField.key].setValue(
        integrationTimeValueA,
      );
      if (specGainBaseValueA !== null) {
        this.experimentControlsForm.controls[specGainField.key].setValue(
          `${specGainBaseValueA}${suffix}`,
        );
      }
    });
    this.experimentControlsForm.markAsDirty();
  }

  copyOxygenValues(): void {
    const salinityValueA = this.f[this.salinityAField.key].value as number;
    [this.salinityBField, this.salinityCField, this.salinityDField].forEach(
      (field) => {
        this.experimentControlsForm.controls[field.key].setValue(
          salinityValueA,
        );
      },
    );
    this.experimentControlsForm.markAsDirty();
  }

  copyPHChemostatValues(): void {
    const inputTypeChemoValueA = this.f[this.inputTypeChemoAField.key].value as
      | string
      | null;
    const inputTypeChemoBaseValueA = inputTypeChemoValueA?.slice(0, -1) ?? null;
    this.experimentControlsForm.controls[
      this.phChemoControlBField.key
    ].setValue(this.f[this.phChemoControlAField.key].value as boolean);
    this.experimentControlsForm.controls[this.targetPHChemoBField.key].setValue(
      this.f[this.targetPHChemoAField.key].value as number,
    );
    this.experimentControlsForm.controls[
      this.pumpDurationChemoBField.key
    ].setValue(this.f[this.pumpDurationChemoAField.key].value as number);
    this.experimentControlsForm.controls[this.flowRatePHBField.key].setValue(
      this.f[this.flowRatePHAField.key].value as number,
    );
    if (inputTypeChemoBaseValueA !== null) {
      this.experimentControlsForm.controls[
        this.inputTypeChemoBField.key
      ].setValue(`${inputTypeChemoBaseValueA}B`);
    }
    this.experimentControlsForm.markAsDirty();
  }

  copyPHDoubleValues(): void {
    this.experimentControlsForm.controls[this.ph2ControlBField.key].setValue(
      this.f[this.ph2ControlAField.key].value as boolean,
    );
    this.experimentControlsForm.controls[this.targetPH2BField.key].setValue(
      this.f[this.targetPH2AField.key].value as number,
    );
    this.experimentControlsForm.controls[this.pumpDuration2BField.key].setValue(
      this.f[this.pumpDuration2AField.key].value as number,
    );
    this.experimentControlsForm.markAsDirty();
  }

  copyMorbidostatValues(): void {
    const activeValue = this.f[this.morbidostatAActiveField.key].value as boolean;
    const thresholdValue = this.f[this.thresholdODAField.key].value as number;
    const dilutionPeriodValue = this.f[this.dilutionPeriodAField.key].value as number;
    const dilutionFactorValue = this.f[this.dilutionFactorAField.key].value as number;
    const targets: [
      FormBase<boolean>,
      FormBase<number>,
      FormBase<number>,
      FormBase<number>,
    ][] = [
      [
        this.morbidostatBActiveField,
        this.thresholdODBField,
        this.dilutionPeriodBField,
        this.dilutionFactorBField,
      ],
      [
        this.morbidostatCActiveField,
        this.thresholdODCField,
        this.dilutionPeriodCField,
        this.dilutionFactorCField,
      ],
      [
        this.morbidostatDActiveField,
        this.thresholdODDField,
        this.dilutionPeriodDField,
        this.dilutionFactorDField,
      ],
    ];
    for (const [active, threshold, dilutionPeriod, dilutionFactor] of targets) {
      this.experimentControlsForm.controls[active.key].setValue(activeValue);
      this.experimentControlsForm.controls[threshold.key].setValue(thresholdValue);
      this.experimentControlsForm.controls[dilutionPeriod.key].setValue(dilutionPeriodValue);
      this.experimentControlsForm.controls[dilutionFactor.key].setValue(dilutionFactorValue);
    }
    this.experimentControlsForm.markAsDirty();
  }

  copyPHValues(): void {
    const phControlValueA = this.f[this.phControlAField.key].value as boolean;
    const targetPHValueA = this.f[this.targetPHAField.key].value as number;
    const pumpDurationValueA = this.f[this.pumpDurationAField.key]
      .value as number;
    const inputTypeValueA = this.f[this.inputTypeAField.key].value as
      | string
      | null;
    const inputTypeBaseValueA = inputTypeValueA?.slice(0, -1) ?? null;
    const targets: [
      FormBase<boolean>,
      FormBase<number>,
      FormBase<number>,
      FormBase<string>,
      string,
    ][] = [
      [
        this.phControlBField,
        this.targetPHBField,
        this.pumpDurationBField,
        this.inputTypeBField,
        'B',
      ],
      [
        this.phControlCField,
        this.targetPHCField,
        this.pumpDurationCField,
        this.inputTypeCField,
        'C',
      ],
      [
        this.phControlDField,
        this.targetPHDField,
        this.pumpDurationDField,
        this.inputTypeDField,
        'D',
      ],
    ];
    targets.forEach(
      ([
        phControlField,
        targetPHField,
        pumpDurationField,
        inputTypeField,
        suffix,
      ]) => {
        this.experimentControlsForm.controls[phControlField.key].setValue(
          phControlValueA,
        );
        this.experimentControlsForm.controls[targetPHField.key].setValue(
          targetPHValueA,
        );
        this.experimentControlsForm.controls[pumpDurationField.key].setValue(
          pumpDurationValueA,
        );
        if (inputTypeBaseValueA !== null) {
          this.experimentControlsForm.controls[inputTypeField.key].setValue(
            `${inputTypeBaseValueA}${suffix}`,
          );
        }
      },
    );
    this.experimentControlsForm.markAsDirty();
  }

  copySpargingValues(): void {
    const typeValueA = this.f[this.spargingControlTypeAField.key].value as string;
    const baseTypeValue = typeValueA.slice(0, -1);
    const airflowValueA = this.f[this.airflowAField.key].value as number;
    const targets: [FormBase<string>, FormBase<number>, string][] = [
      [this.spargingControlTypeBField, this.airflowBField, 'B'],
      [this.spargingControlTypeCField, this.airflowCField, 'C'],
      [this.spargingControlTypeDField, this.airflowDField, 'D'],
    ];
    targets.forEach(([typeField, airflowField, suffix]) => {
      this.experimentControlsForm.controls[typeField.key].setValue(
        `${baseTypeValue}${suffix}`,
      );
      this.experimentControlsForm.controls[airflowField.key].setValue(
        airflowValueA,
      );
    });
    this.experimentControlsForm.markAsDirty();
  }

  copyStirringSpeedValues(): void {
    const setSpeedValueA = this.f[this.setStirringSpeedAField.key]
      .value as boolean;
    const speedValueA = this.f[this.speedAField.key].value as number;
    const targets: [FormBase<boolean>, FormBase<number>][] = [
      [this.setStirringSpeedBField, this.speedBField],
      [this.setStirringSpeedCField, this.speedCField],
      [this.setStirringSpeedDField, this.speedDField],
    ];
    targets.forEach(([setSpeedField, speedField]) => {
      this.experimentControlsForm.controls[setSpeedField.key].setValue(
        setSpeedValueA,
      );
      this.experimentControlsForm.controls[speedField.key].setValue(
        speedValueA,
      );
    });
    this.experimentControlsForm.markAsDirty();
  }

  copyTurbidostatControlValues(): void {
    const turbActiveValueA = this.f[this.turbAActiveField.key].value as boolean;
    const targetODValueA = this.f[this.targetODAField.key].value as number;
    const targets: [FormBase<boolean>, FormBase<number>][] = [
      [this.turbBActiveField, this.targetODBField],
      [this.turbCActiveField, this.targetODCField],
      [this.turbDActiveField, this.targetODDField],
    ];
    targets.forEach(([activeField, targetODField]) => {
      this.experimentControlsForm.controls[activeField.key].setValue(
        turbActiveValueA,
      );
      this.experimentControlsForm.controls[targetODField.key].setValue(
        targetODValueA,
      );
    });
    this.experimentControlsForm.markAsDirty();
  }

  copyTempControlValues(): void {
    const controlValueA = this.f[this.temperatureControlAField.key]
      .value as boolean;
    const tempValueA = this.f[this.temperatureAField.key].value as number;
    const targets: [FormBase<boolean>, FormBase<number>][] = [
      [this.temperatureControlBField, this.temperatureBField],
      [this.temperatureControlCField, this.temperatureCField],
      [this.temperatureControlDField, this.temperatureDField],
    ];
    targets.forEach(([controlField, tempField]) => {
      this.experimentControlsForm.controls[controlField.key].setValue(
        controlValueA,
      );
      this.experimentControlsForm.controls[tempField.key].setValue(tempValueA);
    });
    this.experimentControlsForm.markAsDirty();
  }

  // ── Private helper methods ─────────────────────────────────────────────────

  protected setFlaskControlState(
    flaskLetter: Flask,
    controlType:
      | 'temperature'
      | 'turbidostat'
      | 'chemostat'
      | 'ph'
      | 'ph2'
      | 'phChemo'
      | 'morbidostat',
    enabled: boolean | null,
  ): void {
    const fieldMappings = {
      temperature: {
        A: [this.temperatureAField.key],
        B: [this.temperatureBField.key],
        C: [this.temperatureCField.key],
        D: [this.temperatureDField.key],
      },
      turbidostat: {
        A: [this.targetODAField.key],
        B: [this.targetODBField.key],
        C: [this.targetODCField.key],
        D: [this.targetODDField.key],
      },
      chemostat: {
        A: [this.flowRateAField.key],
        B: [this.flowRateBField.key],
        C: [this.flowRateCField.key],
        D: [this.flowRateDField.key],
      },
      ph: {
        A: [
          this.targetPHAField.key,
          this.inputTypeAField.key,
          this.pumpDurationAField.key,
        ],
        B: [
          this.targetPHBField.key,
          this.inputTypeBField.key,
          this.pumpDurationBField.key,
        ],
        C: [
          this.targetPHCField.key,
          this.inputTypeCField.key,
          this.pumpDurationCField.key,
        ],
        D: [
          this.targetPHDField.key,
          this.inputTypeDField.key,
          this.pumpDurationDField.key,
        ],
      },
      ph2: {
        A: [this.targetPH2AField.key, this.pumpDuration2AField.key],
        B: [this.targetPH2BField.key, this.pumpDuration2BField.key],
        C: [],
        D: [],
      },
      phChemo: {
        A: [
          this.targetPHChemoAField.key,
          this.pumpDurationChemoAField.key,
          this.inputTypeChemoAField.key,
          this.flowRatePHAField.key,
        ],
        B: [
          this.targetPHChemoBField.key,
          this.pumpDurationChemoBField.key,
          this.inputTypeChemoBField.key,
          this.flowRatePHBField.key,
        ],
        C: [],
        D: [],
      },
      morbidostat: {
        A: [
          this.thresholdODAField.key,
          this.dilutionPeriodAField.key,
          this.dilutionFactorAField.key,
        ],
        B: [
          this.thresholdODBField.key,
          this.dilutionPeriodBField.key,
          this.dilutionFactorBField.key,
        ],
        C: [
          this.thresholdODCField.key,
          this.dilutionPeriodCField.key,
          this.dilutionFactorCField.key,
        ],
        D: [
          this.thresholdODDField.key,
          this.dilutionPeriodDField.key,
          this.dilutionFactorDField.key,
        ],
      },
    };

    const fields = fieldMappings[controlType]?.[flaskLetter] || [];
    const defaultValues = this.getDefaultValuesForControlType(
      controlType,
      flaskLetter,
    );

    let experimentValues: (number | string | undefined)[] = [];
    if (this.getSavedEntity()) {
      experimentValues = this.getExperimentValuesForControlType(
        controlType,
        flaskLetter,
      );
    }

    // Determine the index based on flask letter
    // This is used for control types that have a single value per flask
    // (e.g., temperature, turbidostat, chemostat)
    // For pH modes, we handle multiple fields separately
    const index = Object.keys(fieldMappings[controlType]).indexOf(flaskLetter);

    const isPHModule =
      controlType === 'ph' ||
      controlType === 'ph2' ||
      controlType === 'phChemo';
    const isMorbidostatControlType = controlType === 'morbidostat';

    if (isPHModule || isMorbidostatControlType) {
      fields.forEach((fieldKey, i) => {
        if (enabled) {
          this.f[fieldKey].enable();
          this.f[fieldKey].setValue(experimentValues[i] ?? defaultValues[i]);
        } else {
          this.f[fieldKey].disable();
          this.f[fieldKey].setValue(null);
        }
      });
    } else {
      fields.forEach((fieldKey, i) => {
        if (enabled) {
          this.f[fieldKey].enable();
          this.f[fieldKey].setValue(
            experimentValues[index] ?? defaultValues[index],
          );
        } else {
          this.f[fieldKey].disable();
          this.f[fieldKey].setValue(null);
        }
      });
    }
  }

  protected getDefaultValuesForControlType(
    controlType: string,
    flaskLetter: string,
  ): (number | string)[] {
    const valueMap: Record<
      string,
      (number | string)[] | Record<string, (number | string)[]>
    > = {
      stirringSpeed: [
        this.speedAField.value,
        this.speedBField.value,
        this.speedCField.value,
        this.speedDField.value,
      ],
      temperature: [
        this.temperatureAField.value,
        this.temperatureBField.value,
        this.temperatureCField.value,
        this.temperatureDField.value,
      ],
      turbidostat: [
        this.targetODAField.value,
        this.targetODBField.value,
        this.targetODCField.value,
        this.targetODDField.value,
      ],
      chemostat: [
        this.flowRateAField.value,
        this.flowRateBField.value,
        this.flowRateCField.value,
        this.flowRateDField.value,
      ],
      ph: {
        A: [
          this.targetPHAField.value,
          this.getInputTypeDefaultForFlask(flaskLetter, false),
          this.pumpDurationAField.value,
        ],
        B: [
          this.targetPHBField.value,
          this.getInputTypeDefaultForFlask(flaskLetter, false),
          this.pumpDurationBField.value,
        ],
        C: [
          this.targetPHCField.value,
          this.getInputTypeDefaultForFlask(flaskLetter, false),
          this.pumpDurationCField.value,
        ],
        D: [
          this.targetPHDField.value,
          this.getInputTypeDefaultForFlask(flaskLetter, false),
          this.pumpDurationDField.value,
        ],
      },
      ph2: {
        A: [this.targetPH2AField.value, this.pumpDuration2AField.value],
        B: [this.targetPH2BField.value, this.pumpDuration2BField.value],
      },
      phChemo: {
        A: [
          this.targetPHChemoAField.value,
          this.pumpDurationChemoAField.value,
          this.getInputTypeDefaultForFlask(flaskLetter, true),
          this.flowRatePHAField.value,
        ],
        B: [
          this.targetPHBField.value,
          this.pumpDurationBField.value,
          this.getInputTypeDefaultForFlask(flaskLetter, true),
          this.flowRatePHBField.value,
        ],
      },
      sparging: [
        this.flowRateAField.value,
        this.flowRateBField.value,
        this.flowRateCField.value,
        this.flowRateDField.value,
      ],
      morbidostat: {
        A: [
          this.thresholdODAField.value,
          this.dilutionPeriodAField.value,
          this.dilutionFactorAField.value,
        ],
        B: [
          this.thresholdODBField.value,
          this.dilutionPeriodBField.value,
          this.dilutionFactorBField.value,
        ],
        C: [
          this.thresholdODCField.value,
          this.dilutionPeriodCField.value,
          this.dilutionFactorCField.value,
        ],
        D: [
          this.thresholdODDField.value,
          this.dilutionPeriodDField.value,
          this.dilutionFactorDField.value,
        ],
      },
    };

    if (
      controlType === 'ph' ||
      controlType === 'ph2' ||
      controlType === 'phChemo' ||
      controlType === 'morbidostat'
    ) {
      return (
        (valueMap[controlType] as Record<string, (number | string)[]>)[
          flaskLetter
        ] || []
      );
    } else {
      return (valueMap[controlType] as (number | string)[]) || [];
    }
  }

  protected getExperimentValuesForControlType(
    controlType: string,
    flaskLetter: string,
  ): (number | string | undefined)[] {
    const entity = this.getSavedEntity();
    const valueMap: Record<
      string,
      | (number | string | undefined)[]
      | Record<string, (number | string | undefined)[]>
    > = {
      stirringSpeed: [
        entity.speedRPMA,
        entity.speedRPMB,
        entity.speedRPMC,
        entity.speedRPMD,
      ],
      temperature: [
        entity.cultureTemperatureA,
        entity.cultureTemperatureB,
        entity.cultureTemperatureC,
        entity.cultureTemperatureD,
      ],
      turbidostat: [
        entity.targetODA,
        entity.targetODB,
        entity.targetODC,
        entity.targetODD,
      ],
      chemostat: [
        entity.flowRateA,
        entity.flowRateB,
        entity.flowRateC,
        entity.flowRateD,
      ],
      ph: {
        A: [
          entity.targetPHA,
          entity.inputTypeA ? 'BaseA' : 'AcidA',
          entity.pumpDurationA,
        ],
        B: [
          entity.targetPHB,
          entity.inputTypeB ? 'BaseB' : 'AcidB',
          entity.pumpDurationB,
        ],
        C: [
          entity.targetPHC,
          entity.inputTypeC ? 'BaseC' : 'AcidC',
          entity.pumpDurationC,
        ],
        D: [
          entity.targetPHD,
          entity.inputTypeD ? 'BaseD' : 'AcidD',
          entity.pumpDurationD,
        ],
      },
      ph2: {
        A: [entity.targetPHA, entity.pumpDurationA],
        B: [entity.targetPHB, entity.pumpDurationB],
      },
      phChemo: {
        A: [
          entity.targetPHA,
          entity.pumpDurationA,
          entity.inputTypeA ? 'ChemoBaseA' : 'ChemoAcidA',
          entity.flowRateA,
        ],
        B: [
          entity.targetPHB,
          entity.pumpDurationB,
          entity.inputTypeB ? 'ChemoBaseB' : 'ChemoAcidB',
          entity.flowRateB,
        ],
      },
      sparging: [
        entity.airFlowA,
        entity.airFlowB,
        entity.airFlowC,
        entity.airFlowD,
      ],
      morbidostat: {
        A: [entity.thresholdODA, entity.dilutionPeriodA, entity.dilutionFactorA],
        B: [entity.thresholdODB, entity.dilutionPeriodB, entity.dilutionFactorB],
        C: [entity.thresholdODC, entity.dilutionPeriodC, entity.dilutionFactorC],
        D: [entity.thresholdODD, entity.dilutionPeriodD, entity.dilutionFactorD],
      },
    };

    if (
      controlType === 'ph' ||
      controlType === 'ph2' ||
      controlType === 'phChemo' ||
      controlType === 'morbidostat'
    ) {
      return (
        (
          valueMap[controlType] as Record<
            string,
            (number | string | undefined)[]
          >
        )[flaskLetter] || []
      );
    } else {
      return (valueMap[controlType] as (number | string | undefined)[]) || [];
    }
  }

  private getInputTypeDefaultForFlask(
    flaskLetter: string,
    isChemo: boolean,
  ): string {
    // Map to the appropriate field's default value based on flask and mode
    // Note: pH 2x mode doesn't use input types, only regular pH and pH + Chemostat modes do
    if (isChemo) {
      // pH + Chemostat mode only supports flasks A and B
      const chemoFieldMap: Record<string, string> = {
        A: this.inputTypeChemoAField.value,
        B: this.inputTypeChemoBField.value,
      };
      return chemoFieldMap[flaskLetter] || this.inputTypeChemoAField.value; // fallback to A if invalid flask
    } else {
      // Regular pH mode supports all flasks A, B, C, D
      const fieldMap: Record<string, string> = {
        A: this.inputTypeAField.value,
        B: this.inputTypeBField.value,
        C: this.inputTypeCField.value,
        D: this.inputTypeDField.value,
      };
      return fieldMap[flaskLetter] || `Acid${flaskLetter}`;
    }
  }

  protected updateStirringSpeedState(
    flaskLetter: Flask,
    setToZero: boolean,
  ): void {
    const speedFieldMap = {
      A: this.speedAField,
      B: this.speedBField,
      C: this.speedCField,
      D: this.speedDField,
    };

    const speedField = speedFieldMap[flaskLetter];
    const fieldKey = speedField.key;

    if (setToZero) {
      this.f[fieldKey].setValue(0);
      this.f[fieldKey].disable();
    } else {
      this.f[fieldKey].setValue(speedField.value);
      this.f[fieldKey].enable();
    }
  }

  // Extract experiment mode checking
  protected isExperimentMode(mode: ExperimentMode): boolean {
    return this.f[this.experimentModeField.key].value === mode;
  }

  protected isTurbidostatMode(): boolean {
    return this.isExperimentMode(ExperimentMode.Turbidostat);
  }

  protected isChemostatMode(): boolean {
    return this.isExperimentMode(ExperimentMode.Chemostat);
  }

  protected isPHControlMode(): boolean {
    return this.isExperimentMode(ExperimentMode.PHControl);
  }

  protected isPHControlDoubleMode(): boolean {
    return this.isExperimentMode(ExperimentMode.PHControlDouble);
  }

  protected isPHControlChemostatMode(): boolean {
    return this.isExperimentMode(ExperimentMode.PHControlChemostat);
  }

  protected isMorbidostatMode(): boolean {
    return this.isExperimentMode(ExperimentMode.Morbidostat);
  }

  private updateRequiredModules(mode: ExperimentMode): void {
    // Remove existing liquid control and pH modules
    this.requiredDeviceModules.update((modules: Module[]) =>
      modules.filter(
        (module) => module !== Module.LiquidControl && module !== Module.pH,
      ),
    );

    const pHControlIndex = this.moduleCheckboxesField.options.findIndex(
      (option) => option.key === 'pH',
    );

    switch (mode) {
      case ExperimentMode.BatchCulture:
        this.checkboxesFormArray.controls[pHControlIndex].enable();
        break;

      case ExperimentMode.Chemostat:
      case ExperimentMode.Turbidostat:
      case ExperimentMode.Morbidostat:
        this.requiredDeviceModules.update((modules: Module[]) => [
          ...modules,
          Module.LiquidControl,
        ]);
        this.checkboxesFormArray.controls[pHControlIndex].enable();
        break;

      case ExperimentMode.PHControl:
      case ExperimentMode.PHControlDouble:
      case ExperimentMode.PHControlChemostat:
        this.requiredDeviceModules.update((modules: Module[]) => [
          ...modules,
          Module.LiquidControl,
          Module.pH,
        ]);
        this.usePH.set(true);
        this.checkboxesFormArray.controls[pHControlIndex].setValue(true);
        this.checkboxesFormArray.controls[pHControlIndex].disable();
        break;
    }
  }

  private updateModuleFromCheckbox(
    moduleName: string,
    isChecked: boolean,
  ): void {
    const moduleMap: Record<
      string,
      { module: Module; setFlag: (v: boolean) => void }
    > = {
      Oxygen: { module: Module.Oxygen, setFlag: (v) => this.useOxygen.set(v) },
      Fluorescence: {
        module: Module.Fluorescence,
        setFlag: (v) => this.useFluorescence.set(v),
      },
      pH: { module: Module.pH, setFlag: (v) => this.usePH.set(v) },
      Sparging: {
        module: Module.Sparging,
        setFlag: (v) => this.useSparging.set(v),
      },
    };

    const config = moduleMap[moduleName];
    if (!config) return;

    config.setFlag(isChecked);

    if (isChecked) {
      this.requiredDeviceModules.update((modules: Module[]) => [
        ...modules,
        config.module,
      ]);
      if (moduleName === 'Fluorescence') {
        this.setFluorescenceFieldsState(true);
        this.setFluorescenceFieldValues();
      }
    } else {
      this.requiredDeviceModules.update((modules: Module[]) =>
        modules.filter((x) => x !== config.module),
      );
      if (moduleName === 'Fluorescence') {
        this.setFluorescenceFieldsState(false);
      }
    }
  }

  protected setFluorescenceFieldsState(enabled: boolean): void {
    const action = enabled ? 'enable' : 'disable';

    // Integration time fields
    this.f[this.integrationTimeAField.key][action]();
    this.f[this.integrationTimeBField.key][action]();
    this.f[this.integrationTimeCField.key][action]();
    this.f[this.integrationTimeDField.key][action]();

    // Gain fields
    this.f[this.specGainAField.key][action]();
    this.f[this.specGainBField.key][action]();
    this.f[this.specGainCField.key][action]();
    this.f[this.specGainDField.key][action]();

    // Channels
    this.f[this.fluorescenceChannelsField.key][action]();

    if (!enabled) {
      this.f[this.integrationTimeAField.key].setValue(null);
      this.f[this.integrationTimeBField.key].setValue(null);
      this.f[this.integrationTimeCField.key].setValue(null);
      this.f[this.integrationTimeDField.key].setValue(null);

      this.f[this.specGainAField.key].setValue(null);
      this.f[this.specGainBField.key].setValue(null);
      this.f[this.specGainCField.key].setValue(null);
      this.f[this.specGainDField.key].setValue(null);

      this.selectedFluorescenceChannels.set([]);
      this.fluorescenceChannelsField.options = this.fluorescenceChannels().map((channel) => ({
        key: channel.memorySlot,
        value: channel.channelName,
      }));
    }
  }

  private setFluorescenceFieldValues(): void {
    const entity = this.getSavedEntity();
    this.f[this.integrationTimeAField.key].setValue(entity?.integrationTimeA ?? this.integrationTimeAField.value);
    this.f[this.integrationTimeBField.key].setValue(entity?.integrationTimeB ?? this.integrationTimeBField.value);
    this.f[this.integrationTimeCField.key].setValue(entity?.integrationTimeC ?? this.integrationTimeCField.value);
    this.f[this.integrationTimeDField.key].setValue(entity?.integrationTimeD ?? this.integrationTimeDField.value);
    this.f[this.specGainAField.key].setValue(convertSpecGainToString(entity?.specGainA, 'A') ?? this.specGainAField.value);
    this.f[this.specGainBField.key].setValue(convertSpecGainToString(entity?.specGainB, 'B') ?? this.specGainBField.value);
    this.f[this.specGainCField.key].setValue(convertSpecGainToString(entity?.specGainC, 'C') ?? this.specGainCField.value);
    this.f[this.specGainDField.key].setValue(convertSpecGainToString(entity?.specGainD, 'D') ?? this.specGainDField.value);
    if (entity?.fluorescenceChannels?.length) {
      this.selectedFluorescenceChannels.set(entity.fluorescenceChannels);
      this.filterFluorescenceChannelOptions();
    }
  }

  // ── Form value extraction helpers ──────────────────────────────────────────

  protected getBasicFormValues(
    experimentData:
      | CreateExperiment
      | Experiment
      | ExperimentTemplate
      | ExperimentUpdate,
    preserveCurrentMode = false,
  ) {
    return {
      [this.experimentModeField.key]: preserveCurrentMode
        ? this.f[this.experimentModeField.key].value
        : experimentData.experimentMode,
      [this.experimentNameField.key]: experimentData.name,
      [this.organismField.key]: experimentData.organism,
      [this.strainField.key]: experimentData.strain,
      [this.mediaField.key]: experimentData.media,
      [this.enableNotificationsField.key]:
        experimentData.enableNotifications ?? false,
      [this.notificationEmailField.key]: experimentData.notificationEmail,
    };
  }

  protected getDefaultModuleFormValues(
    experimentData:
      | CreateExperiment
      | Experiment
      | ExperimentTemplate
      | ExperimentUpdate,
  ) {
    return {
      [this.ambientTemperatureField.key]: experimentData.ambientTemperature,
      [this.temperatureControlAField.key]: experimentData.temperatureControlA,
      [this.temperatureControlBField.key]: experimentData.temperatureControlB,
      [this.temperatureControlCField.key]: experimentData.temperatureControlC,
      [this.temperatureControlDField.key]: experimentData.temperatureControlD,
      [this.temperatureAField.key]: experimentData.cultureTemperatureA,
      [this.temperatureBField.key]: experimentData.cultureTemperatureB,
      [this.temperatureCField.key]: experimentData.cultureTemperatureC,
      [this.temperatureDField.key]: experimentData.cultureTemperatureD,
      [this.samplingTimeField.key]: experimentData.samplingInterval,
      [this.experimentTimeField.key]: experimentData.duration,
      [this.setStirringSpeedAField.key]: experimentData.speedRPMA === 0,
      [this.setStirringSpeedBField.key]: experimentData.speedRPMB === 0,
      [this.setStirringSpeedCField.key]: experimentData.speedRPMC === 0,
      [this.setStirringSpeedDField.key]: experimentData.speedRPMD === 0,
      [this.speedAField.key]: experimentData.speedRPMA,
      [this.speedBField.key]: experimentData.speedRPMB,
      [this.speedCField.key]: experimentData.speedRPMC,
      [this.speedDField.key]: experimentData.speedRPMD,
    };
  }

  protected getTurbidostatFormValues(
    experimentData:
      | CreateExperiment
      | Experiment
      | ExperimentTemplate
      | ExperimentUpdate,
  ) {
    return {
      [this.maintainODField.key]:
        experimentData.maintainOD ?? this.maintainODField.value,
      [this.turbAActiveField.key]:
        experimentData.turbAActive ?? this.turbAActiveField.value,
      [this.turbBActiveField.key]:
        experimentData.turbBActive ?? this.turbBActiveField.value,
      [this.turbCActiveField.key]:
        experimentData.turbCActive ?? this.turbCActiveField.value,
      [this.turbDActiveField.key]:
        experimentData.turbDActive ?? this.turbDActiveField.value,
      [this.targetODAField.key]: experimentData.targetODA ?? this.targetODAField.value,
      [this.targetODBField.key]: experimentData.targetODB ?? this.targetODBField.value,
      [this.targetODCField.key]: experimentData.targetODC ?? this.targetODCField.value,
      [this.targetODDField.key]: experimentData.targetODD ?? this.targetODDField.value,
    };
  }

  protected getChemostatFormValues(
    experimentData:
      | CreateExperiment
      | Experiment
      | ExperimentTemplate
      | ExperimentUpdate,
  ) {
    return {
      [this.chemoAActiveField.key]:
        experimentData.chemoAActive ?? this.chemoAActiveField.value,
      [this.chemoBActiveField.key]:
        experimentData.chemoBActive ?? this.chemoBActiveField.value,
      [this.chemoCActiveField.key]:
        experimentData.chemoCActive ?? this.chemoCActiveField.value,
      [this.chemoDActiveField.key]:
        experimentData.chemoDActive ?? this.chemoDActiveField.value,
      [this.flowRateAField.key]: experimentData.flowRateA ?? this.flowRateAField.value,
      [this.flowRateBField.key]: experimentData.flowRateB ?? this.flowRateBField.value,
      [this.flowRateCField.key]: experimentData.flowRateC ?? this.flowRateCField.value,
      [this.flowRateDField.key]: experimentData.flowRateD ?? this.flowRateDField.value,
    };
  }

  protected getMorbidostatFormValues(
    experimentData:
      | CreateExperiment
      | Experiment
      | ExperimentTemplate
      | ExperimentUpdate,
  ) {
    return {
      [this.morbidostatAActiveField.key]:
        experimentData.morbidostatAActive ?? this.morbidostatAActiveField.value,
      [this.morbidostatBActiveField.key]:
        experimentData.morbidostatBActive ?? this.morbidostatBActiveField.value,
      [this.morbidostatCActiveField.key]:
        experimentData.morbidostatCActive ?? this.morbidostatCActiveField.value,
      [this.morbidostatDActiveField.key]:
        experimentData.morbidostatDActive ?? this.morbidostatDActiveField.value,
      [this.thresholdODAField.key]: experimentData.thresholdODA ?? this.thresholdODAField.value,
      [this.thresholdODBField.key]: experimentData.thresholdODB ?? this.thresholdODBField.value,
      [this.thresholdODCField.key]: experimentData.thresholdODC ?? this.thresholdODCField.value,
      [this.thresholdODDField.key]: experimentData.thresholdODD ?? this.thresholdODDField.value,
      [this.dilutionPeriodAField.key]: experimentData.dilutionPeriodA ?? this.dilutionPeriodAField.value,
      [this.dilutionPeriodBField.key]: experimentData.dilutionPeriodB ?? this.dilutionPeriodBField.value,
      [this.dilutionPeriodCField.key]: experimentData.dilutionPeriodC ?? this.dilutionPeriodCField.value,
      [this.dilutionPeriodDField.key]: experimentData.dilutionPeriodD ?? this.dilutionPeriodDField.value,
      [this.dilutionFactorAField.key]: experimentData.dilutionFactorA ?? this.dilutionFactorAField.value,
      [this.dilutionFactorBField.key]: experimentData.dilutionFactorB ?? this.dilutionFactorBField.value,
      [this.dilutionFactorCField.key]: experimentData.dilutionFactorC ?? this.dilutionFactorCField.value,
      [this.dilutionFactorDField.key]: experimentData.dilutionFactorD ?? this.dilutionFactorDField.value,
    };
  }

  protected getPHControlFormValues(
    experimentData:
      | CreateExperiment
      | Experiment
      | ExperimentTemplate
      | ExperimentUpdate,
  ) {
    return {
      // Standard pH controls
      [this.phControlAField.key]:
        experimentData.phControlA ?? this.phControlAField.value,
      [this.phControlBField.key]:
        experimentData.phControlB ?? this.phControlBField.value,
      [this.phControlCField.key]:
        experimentData.phControlC ?? this.phControlCField.value,
      [this.phControlDField.key]:
        experimentData.phControlD ?? this.phControlDField.value,
      [this.targetPHAField.key]: experimentData.targetPHA ?? this.targetPHAField.value,
      [this.targetPHBField.key]: experimentData.targetPHB ?? this.targetPHBField.value,
      [this.targetPHCField.key]: experimentData.targetPHC ?? this.targetPHCField.value,
      [this.targetPHDField.key]: experimentData.targetPHD ?? this.targetPHDField.value,
      [this.inputTypeAField.key]:
        convertBooleanToInputType(experimentData.inputTypeA, 'A') ??
        this.inputTypeAField.value,
      [this.inputTypeBField.key]:
        convertBooleanToInputType(experimentData.inputTypeB, 'B') ??
        this.inputTypeBField.value,
      [this.inputTypeCField.key]:
        convertBooleanToInputType(experimentData.inputTypeC, 'C') ??
        this.inputTypeCField.value,
      [this.inputTypeDField.key]:
        convertBooleanToInputType(experimentData.inputTypeD, 'D') ??
        this.inputTypeDField.value,
      [this.pumpDurationAField.key]: experimentData.pumpDurationA ?? this.pumpDurationAField.value,
      [this.pumpDurationBField.key]: experimentData.pumpDurationB ?? this.pumpDurationBField.value,
      [this.pumpDurationCField.key]: experimentData.pumpDurationC ?? this.pumpDurationCField.value,
      [this.pumpDurationDField.key]: experimentData.pumpDurationD ?? this.pumpDurationDField.value,
      // pH 2x controls
      [this.ph2ControlAField.key]:
        experimentData.phControlA ?? this.ph2ControlAField.value,
      [this.ph2ControlBField.key]:
        experimentData.phControlB ?? this.ph2ControlBField.value,
      [this.targetPH2AField.key]: experimentData.targetPHA ?? this.targetPH2AField.value,
      [this.targetPH2BField.key]: experimentData.targetPHB ?? this.targetPH2BField.value,
      [this.pumpDuration2AField.key]: experimentData.pumpDurationA ?? this.pumpDuration2AField.value,
      [this.pumpDuration2BField.key]: experimentData.pumpDurationB ?? this.pumpDuration2BField.value,
      // pH + chemostat controls
      [this.phChemoControlAField.key]:
        experimentData.chemoAActive ?? this.phChemoControlAField.value,
      [this.phChemoControlBField.key]:
        experimentData.chemoBActive ?? this.phChemoControlBField.value,
      [this.targetPHChemoAField.key]: experimentData.targetPHA ?? this.targetPHChemoAField.value,
      [this.targetPHChemoBField.key]: experimentData.targetPHB ?? this.targetPHChemoBField.value,
      [this.inputTypeChemoAField.key]:
        convertBooleanToInputType(experimentData.inputTypeA, 'A', true) ??
        this.inputTypeChemoAField.value,
      [this.inputTypeChemoBField.key]:
        convertBooleanToInputType(experimentData.inputTypeB, 'B', true) ??
        this.inputTypeChemoBField.value,
      [this.pumpDurationChemoAField.key]: experimentData.pumpDurationA ?? this.pumpDurationChemoAField.value,
      [this.pumpDurationChemoBField.key]: experimentData.pumpDurationB ?? this.pumpDurationChemoBField.value,
      [this.flowRatePHAField.key]: experimentData.flowRateA ?? this.flowRatePHAField.value,
      [this.flowRatePHBField.key]: experimentData.flowRateB ?? this.flowRatePHBField.value,
    };
  }

  protected getFluorescenceFormValues(
    experimentData:
      | CreateExperiment
      | Experiment
      | ExperimentTemplate
      | ExperimentUpdate,
  ) {
    return {
      // LED controls are always included (they're separate from fluorescence measurement)
      [this.led1OnField.key]: experimentData.led1On ?? this.led1OnField.value,
      [this.led2OnField.key]: experimentData.led2On ?? this.led2OnField.value,
      [this.led3OnField.key]: experimentData.led3On ?? this.led3OnField.value,
      [this.led1IntensityField.key]:
        experimentData.led1Intensity ?? this.led1IntensityField.value,
      [this.led2IntensityField.key]:
        experimentData.led2Intensity ?? this.led2IntensityField.value,
      [this.led3IntensityField.key]:
        experimentData.led3Intensity ?? this.led3IntensityField.value,
      // Fluorescence measurement fields - only include if useFluorescence is true
      ...(experimentData.useFluorescence
        ? {
            [this.integrationTimeAField.key]: experimentData.integrationTimeA ?? this.integrationTimeAField.value,
            [this.integrationTimeBField.key]: experimentData.integrationTimeB ?? this.integrationTimeBField.value,
            [this.integrationTimeCField.key]: experimentData.integrationTimeC ?? this.integrationTimeCField.value,
            [this.integrationTimeDField.key]: experimentData.integrationTimeD ?? this.integrationTimeDField.value,
            [this.specGainAField.key]:
              convertSpecGainToString(experimentData.specGainA, 'A') ?? this.specGainAField.value,
            [this.specGainBField.key]:
              convertSpecGainToString(experimentData.specGainB, 'B') ?? this.specGainBField.value,
            [this.specGainCField.key]:
              convertSpecGainToString(experimentData.specGainC, 'C') ?? this.specGainCField.value,
            [this.specGainDField.key]:
              convertSpecGainToString(experimentData.specGainD, 'D') ?? this.specGainDField.value,
          }
        : {}),
    };
  }

  protected getOxygenFormValues(
    experimentData:
      | CreateExperiment
      | Experiment
      | ExperimentTemplate
      | ExperimentUpdate,
  ) {
    return {
      [this.salinityAField.key]:
        experimentData.salinityA ?? this.salinityAField.value,
      [this.salinityBField.key]:
        experimentData.salinityB ?? this.salinityBField.value,
      [this.salinityCField.key]:
        experimentData.salinityC ?? this.salinityCField.value,
      [this.salinityDField.key]:
        experimentData.salinityD ?? this.salinityDField.value,
    };
  }

  protected getSpargingFormValues(
    experimentData:
      | CreateExperiment
      | Experiment
      | ExperimentTemplate
      | ExperimentUpdate,
  ): Record<string, string | number | undefined> {
    return {
      [this.spargingControlTypeAField.key]: experimentData.spargingControlTypeA
        ? convertSpargingStatus(experimentData.spargingControlTypeA as number) +
          'A'
        : this.spargingControlTypeAField.value,
      [this.spargingControlTypeBField.key]: experimentData.spargingControlTypeB
        ? convertSpargingStatus(experimentData.spargingControlTypeB as number) +
          'B'
        : this.spargingControlTypeBField.value,
      [this.spargingControlTypeCField.key]: experimentData.spargingControlTypeC
        ? convertSpargingStatus(experimentData.spargingControlTypeC as number) +
          'C'
        : this.spargingControlTypeCField.value,
      [this.spargingControlTypeDField.key]: experimentData.spargingControlTypeD
        ? convertSpargingStatus(experimentData.spargingControlTypeD as number) +
          'D'
        : this.spargingControlTypeDField.value,
      [this.airflowAField.key]: experimentData.airFlowA ?? this.airflowAField.value,
      [this.airflowBField.key]: experimentData.airFlowB ?? this.airflowBField.value,
      [this.airflowCField.key]: experimentData.airFlowC ?? this.airflowCField.value,
      [this.airflowDField.key]: experimentData.airFlowD ?? this.airflowDField.value,
    };
  }

  // Form value extraction (live form → API payload)
  // Use undefined so absent fields are omitted from the JSON body; the backend treats absent as 'preserve existing value' 
  protected getTurbidostatValues() {
    if (!this.isTurbidostatMode()) {
      return {
        maintainOD: undefined,
        targetODA: undefined,
        targetODB: undefined,
        targetODC: undefined,
        targetODD: undefined,
        turbAActive: undefined,
        turbBActive: undefined,
        turbCActive: undefined,
        turbDActive: undefined,
      };
    }

    const maintainOD = this.f[this.maintainODField.key].value as boolean;
    const turbAActive = this.f[this.turbAActiveField.key].value as boolean;
    const turbBActive = this.f[this.turbBActiveField.key].value as boolean;
    const turbCActive = this.f[this.turbCActiveField.key].value as boolean;
    const turbDActive = this.f[this.turbDActiveField.key].value as boolean;

    return {
      maintainOD,
      targetODA:
        maintainOD || !turbAActive
          ? undefined
          : (this.f[this.targetODAField.key].value as number),
      targetODB:
        maintainOD || !turbBActive
          ? undefined
          : (this.f[this.targetODBField.key].value as number),
      targetODC:
        maintainOD || !turbCActive
          ? undefined
          : (this.f[this.targetODCField.key].value as number),
      targetODD:
        maintainOD || !turbDActive
          ? undefined
          : (this.f[this.targetODDField.key].value as number),
      turbAActive,
      turbBActive,
      turbCActive,
      turbDActive,
    };
  }

  protected getChemostatValues() {
    const isChemostat = this.isChemostatMode();
    const isPHChemostat = this.isPHControlChemostatMode();

    if (!isChemostat && !isPHChemostat) {
      return {
        chemoAActive: undefined,
        chemoBActive: undefined,
        chemoCActive: undefined,
        chemoDActive: undefined,
        flowRateA: undefined,
        flowRateB: undefined,
        flowRateC: undefined,
        flowRateD: undefined,
      };
    }

    const chemoAActive = isChemostat
      ? (this.f[this.chemoAActiveField.key].value as boolean)
      : (this.f[this.phChemoControlAField.key].value as boolean);
    const chemoBActive = isChemostat
      ? (this.f[this.chemoBActiveField.key].value as boolean)
      : (this.f[this.phChemoControlBField.key].value as boolean);
    const chemoCActive = isChemostat
      ? (this.f[this.chemoCActiveField.key].value as boolean)
      : undefined;
    const chemoDActive = isChemostat
      ? (this.f[this.chemoDActiveField.key].value as boolean)
      : undefined;

    return {
      chemoAActive,
      chemoBActive,
      chemoCActive,
      chemoDActive,
      flowRateA: chemoAActive
        ? isChemostat
          ? (this.f[this.flowRateAField.key].value as number)
          : (this.f[this.flowRatePHAField.key].value as number)
        : undefined,
      flowRateB: chemoBActive
        ? isChemostat
          ? (this.f[this.flowRateBField.key].value as number)
          : (this.f[this.flowRatePHBField.key].value as number)
        : undefined,
      flowRateC:
        isChemostat && chemoCActive
          ? (this.f[this.flowRateCField.key].value as number)
          : undefined,
      flowRateD:
        isChemostat && chemoDActive
          ? (this.f[this.flowRateDField.key].value as number)
          : undefined,
    };
  }

  protected getPHControlValues() {
    const isPHControl = this.isPHControlMode();
    const isPHDouble = this.isPHControlDoubleMode();
    const isPHChemostat = this.isPHControlChemostatMode();

    if (!isPHControl && !isPHDouble && !isPHChemostat) {
      return {
        phControlA: undefined,
        phControlB: undefined,
        phControlC: undefined,
        phControlD: undefined,
        targetPHA: undefined,
        targetPHB: undefined,
        targetPHC: undefined,
        targetPHD: undefined,
        inputTypeA: undefined,
        inputTypeB: undefined,
        inputTypeC: undefined,
        inputTypeD: undefined,
        pumpDurationA: undefined,
        pumpDurationB: undefined,
        pumpDurationC: undefined,
        pumpDurationD: undefined,
      };
    }
    // Determine which fields to use based on mode
    const getControlField = (flask: Flask) => {
      if (isPHControl) {
        const fieldMap = {
          A: this.phControlAField.key,
          B: this.phControlBField.key,
          C: this.phControlCField.key,
          D: this.phControlDField.key,
        };
        return this.f[fieldMap[flask]].value as boolean;
      }
      if (isPHDouble && (flask === 'A' || flask === 'B')) {
        const fieldMap = {
          A: this.ph2ControlAField.key,
          B: this.ph2ControlBField.key,
        };
        return this.f[fieldMap[flask]].value as boolean;
      }
      return undefined;
    };
    const getTargetField = (flask: Flask) => {
      if (isPHControl) {
        const fieldMap = {
          A: this.targetPHAField.key,
          B: this.targetPHBField.key,
          C: this.targetPHCField.key,
          D: this.targetPHDField.key,
        };
        return this.f[fieldMap[flask]].value as number;
      }
      if (isPHDouble && (flask === 'A' || flask === 'B')) {
        const fieldMap = {
          A: this.targetPH2AField.key,
          B: this.targetPH2BField.key,
        };
        return this.f[fieldMap[flask]].value as number;
      }
      if (isPHChemostat && (flask === 'A' || flask === 'B')) {
        const fieldMap = {
          A: this.targetPHChemoAField.key,
          B: this.targetPHChemoBField.key,
        };
        return this.f[fieldMap[flask]].value as number;
      }
      return undefined;
    };
    const getInputType = (flask: Flask) => {
      const controlActive =
        getControlField(flask) ||
        (isPHChemostat &&
          flask in { A: true, B: true } &&
          this.f[
            flask === 'A'
              ? this.phChemoControlAField.key
              : this.phChemoControlBField.key
          ].value);
      if (!controlActive) return undefined;

      if (isPHControl) {
        const fieldMap = {
          A: this.inputTypeAField.key,
          B: this.inputTypeBField.key,
          C: this.inputTypeCField.key,
          D: this.inputTypeDField.key,
        };
        return convertInputTypeToBoolean(
          this.f[fieldMap[flask]].value as string,
        );
      }
      if (isPHChemostat && (flask === 'A' || flask === 'B')) {
        const fieldMap = {
          A: this.inputTypeChemoAField.key,
          B: this.inputTypeChemoBField.key,
        };
        return convertInputTypeToBoolean(
          this.f[fieldMap[flask]].value as string,
        );
      }
      return undefined;
    };
    const getPumpDuration = (flask: Flask) => {
      if (isPHControl) {
        const fieldMap = {
          A: this.pumpDurationAField.key,
          B: this.pumpDurationBField.key,
          C: this.pumpDurationCField.key,
          D: this.pumpDurationDField.key,
        };
        return this.f[fieldMap[flask]].value as number;
      }
      if (isPHDouble && (flask === 'A' || flask === 'B')) {
        const fieldMap = {
          A: this.pumpDuration2AField.key,
          B: this.pumpDuration2BField.key,
        };
        return this.f[fieldMap[flask]].value as number;
      }
      if (isPHChemostat && (flask === 'A' || flask === 'B')) {
        const fieldMap = {
          A: this.pumpDurationChemoAField.key,
          B: this.pumpDurationChemoBField.key,
        };
        return this.f[fieldMap[flask]].value as number;
      }
      return undefined;
    };

    return {
      phControlA: getControlField('A'),
      phControlB: getControlField('B'),
      phControlC: getControlField('C'),
      phControlD: getControlField('D'),
      targetPHA: getTargetField('A'),
      targetPHB: getTargetField('B'),
      targetPHC: getTargetField('C'),
      targetPHD: getTargetField('D'),
      inputTypeA: getInputType('A'),
      inputTypeB: getInputType('B'),
      inputTypeC: getInputType('C'),
      inputTypeD: getInputType('D'),
      pumpDurationA: getPumpDuration('A'),
      pumpDurationB: getPumpDuration('B'),
      pumpDurationC: getPumpDuration('C'),
      pumpDurationD: getPumpDuration('D'),
    };
  }

  protected getMorbidostatValues() {
    if (!this.isMorbidostatMode()) {
      return {
        morbidostatAActive: undefined,
        morbidostatBActive: undefined,
        morbidostatCActive: undefined,
        morbidostatDActive: undefined,
        thresholdODA: undefined,
        thresholdODB: undefined,
        thresholdODC: undefined,
        thresholdODD: undefined,
        dilutionPeriodA: undefined,
        dilutionPeriodB: undefined,
        dilutionPeriodC: undefined,
        dilutionPeriodD: undefined,
        dilutionFactorA: undefined,
        dilutionFactorB: undefined,
        dilutionFactorC: undefined,
        dilutionFactorD: undefined,
      };
    }

    const morbidostatAActive = this.f[this.morbidostatAActiveField.key].value as boolean;
    const morbidostatBActive = this.f[this.morbidostatBActiveField.key].value as boolean;
    const morbidostatCActive = this.f[this.morbidostatCActiveField.key].value as boolean;
    const morbidostatDActive = this.f[this.morbidostatDActiveField.key].value as boolean;

    return {
      morbidostatAActive,
      morbidostatBActive,
      morbidostatCActive,
      morbidostatDActive,
      thresholdODA: morbidostatAActive
        ? (this.f[this.thresholdODAField.key].value as number)
        : undefined,
      thresholdODB: morbidostatBActive
        ? (this.f[this.thresholdODBField.key].value as number)
        : undefined,
      thresholdODC: morbidostatCActive
        ? (this.f[this.thresholdODCField.key].value as number)
        : undefined,
      thresholdODD: morbidostatDActive
        ? (this.f[this.thresholdODDField.key].value as number)
        : undefined,
      dilutionPeriodA: morbidostatAActive
        ? (this.f[this.dilutionPeriodAField.key].value as number)
        : undefined,
      dilutionPeriodB: morbidostatBActive
        ? (this.f[this.dilutionPeriodBField.key].value as number)
        : undefined,
      dilutionPeriodC: morbidostatCActive
        ? (this.f[this.dilutionPeriodCField.key].value as number)
        : undefined,
      dilutionPeriodD: morbidostatDActive
        ? (this.f[this.dilutionPeriodDField.key].value as number)
        : undefined,
      dilutionFactorA: morbidostatAActive
        ? (this.f[this.dilutionFactorAField.key].value as number)
        : undefined,
      dilutionFactorB: morbidostatBActive
        ? (this.f[this.dilutionFactorBField.key].value as number)
        : undefined,
      dilutionFactorC: morbidostatCActive
        ? (this.f[this.dilutionFactorCField.key].value as number)
        : undefined,
      dilutionFactorD: morbidostatDActive
        ? (this.f[this.dilutionFactorDField.key].value as number)
        : undefined,
    };
  }

  protected getFluorescenceValues() {
    return {
      led1On: this.f[this.led1OnField.key].value as boolean,
      led2On: this.f[this.led2OnField.key].value as boolean,
      led3On: this.f[this.led3OnField.key].value as boolean,
      led1Intensity: this.f[this.led1IntensityField.key].value as number,
      led2Intensity: this.f[this.led2IntensityField.key].value as number,
      led3Intensity: this.f[this.led3IntensityField.key].value as number,
      integrationTimeA: this.useFluorescence()
        ? (this.f[this.integrationTimeAField.key].value as number)
        : undefined,
      integrationTimeB: this.useFluorescence()
        ? (this.f[this.integrationTimeBField.key].value as number)
        : undefined,
      integrationTimeC: this.useFluorescence()
        ? (this.f[this.integrationTimeCField.key].value as number)
        : undefined,
      integrationTimeD: this.useFluorescence()
        ? (this.f[this.integrationTimeDField.key].value as number)
        : undefined,
      specGainA: this.useFluorescence()
        ? this.f[this.specGainAField.key].value === 'highA'
        : undefined,
      specGainB: this.useFluorescence()
        ? this.f[this.specGainBField.key].value === 'highB'
        : undefined,
      specGainC: this.useFluorescence()
        ? this.f[this.specGainCField.key].value === 'highC'
        : undefined,
      specGainD: this.useFluorescence()
        ? this.f[this.specGainDField.key].value === 'highD'
        : undefined,
      fluorescenceChannels: this.useFluorescence() ? this.selectedFluorescenceChannels() : undefined,
    };
  }

  protected getOxygenValues() {
    return {
      salinityA: this.useOxygen()
        ? (this.f[this.salinityAField.key].value as number)
        : undefined,
      salinityB: this.useOxygen()
        ? (this.f[this.salinityBField.key].value as number)
        : undefined,
      salinityC: this.useOxygen()
        ? (this.f[this.salinityCField.key].value as number)
        : undefined,
      salinityD: this.useOxygen()
        ? (this.f[this.salinityDField.key].value as number)
        : undefined,
    };
  }

  protected getSpargingValues() {
    return {
      spargingControlTypeA: this.useSparging()
        ? getSpargingStatus(
            this.f[this.spargingControlTypeAField.key].value as string,
          )
        : undefined,
      spargingControlTypeB: this.useSparging()
        ? getSpargingStatus(
            this.f[this.spargingControlTypeBField.key].value as string,
          )
        : undefined,
      spargingControlTypeC: this.useSparging()
        ? getSpargingStatus(
            this.f[this.spargingControlTypeCField.key].value as string,
          )
        : undefined,
      spargingControlTypeD: this.useSparging()
        ? getSpargingStatus(
            this.f[this.spargingControlTypeDField.key].value as string,
          )
        : undefined,
    };
  }

  protected getDefaultFormValues() {
    return {
      // Basic values
      [this.experimentModeField.key]: ExperimentMode.BatchCulture,
      [this.experimentNameField.key]: this.experimentNameField.value,
      [this.organismField.key]: this.organismField.value,
      [this.strainField.key]: this.strainField.value,
      [this.mediaField.key]: this.mediaField.value,
      // Default module values
      [this.ambientTemperatureField.key]: this.ambientTemperatureField.value,
      [this.temperatureControlAField.key]: this.temperatureControlAField.value,
      [this.temperatureControlBField.key]: this.temperatureControlBField.value,
      [this.temperatureControlCField.key]: this.temperatureControlCField.value,
      [this.temperatureControlDField.key]: this.temperatureControlDField.value,
      [this.temperatureAField.key]: this.temperatureAField.value,
      [this.temperatureBField.key]: this.temperatureBField.value,
      [this.temperatureCField.key]: this.temperatureCField.value,
      [this.temperatureDField.key]: this.temperatureDField.value,
      [this.samplingTimeField.key]: this.samplingTimeField.value,
      [this.experimentTimeField.key]: this.experimentTimeField.value,
      [this.speedAField.key]: this.speedAField.value,
      [this.speedBField.key]: this.speedBField.value,
      [this.speedCField.key]: this.speedCField.value,
      [this.speedDField.key]: this.speedDField.value,
      // Turbidostat defaults
      [this.maintainODField.key]: this.maintainODField.value,
      [this.turbAActiveField.key]: this.turbAActiveField.value,
      [this.turbBActiveField.key]: this.turbBActiveField.value,
      [this.turbCActiveField.key]: this.turbCActiveField.value,
      [this.turbDActiveField.key]: this.turbDActiveField.value,
      [this.targetODAField.key]: this.targetODAField.value,
      [this.targetODBField.key]: this.targetODBField.value,
      [this.targetODCField.key]: this.targetODCField.value,
      [this.targetODDField.key]: this.targetODDField.value,
      // Chemostat defaults
      [this.chemoAActiveField.key]: this.chemoAActiveField.value,
      [this.chemoBActiveField.key]: this.chemoBActiveField.value,
      [this.chemoCActiveField.key]: this.chemoCActiveField.value,
      [this.chemoDActiveField.key]: this.chemoDActiveField.value,
      [this.flowRateAField.key]: this.flowRateAField.value,
      [this.flowRateBField.key]: this.flowRateBField.value,
      [this.flowRateCField.key]: this.flowRateCField.value,
      [this.flowRateDField.key]: this.flowRateDField.value,
      // pH control defaults
      [this.phControlAField.key]: this.phControlAField.value,
      [this.phControlBField.key]: this.phControlBField.value,
      [this.phControlCField.key]: this.phControlCField.value,
      [this.phControlDField.key]: this.phControlDField.value,
      [this.targetPHAField.key]: this.targetPHAField.value,
      [this.targetPHBField.key]: this.targetPHBField.value,
      [this.targetPHCField.key]: this.targetPHCField.value,
      [this.targetPHDField.key]: this.targetPHDField.value,
      [this.inputTypeAField.key]: this.inputTypeAField.value,
      [this.inputTypeBField.key]: this.inputTypeBField.value,
      [this.inputTypeCField.key]: this.inputTypeCField.value,
      [this.inputTypeDField.key]: this.inputTypeDField.value,
      [this.pumpDurationAField.key]: this.pumpDurationAField.value,
      [this.pumpDurationBField.key]: this.pumpDurationBField.value,
      [this.pumpDurationCField.key]: this.pumpDurationCField.value,
      [this.pumpDurationDField.key]: this.pumpDurationDField.value,
      // pH 2x defaults
      [this.ph2ControlAField.key]: this.ph2ControlAField.value,
      [this.ph2ControlBField.key]: this.ph2ControlBField.value,
      [this.targetPH2AField.key]: this.targetPH2AField.value,
      [this.targetPH2BField.key]: this.targetPH2BField.value,
      [this.pumpDuration2AField.key]: this.pumpDuration2AField.value,
      [this.pumpDuration2BField.key]: this.pumpDuration2BField.value,
      // pH + chemostat defaults
      [this.phChemoControlAField.key]: this.phChemoControlAField.value,
      [this.phChemoControlBField.key]: this.phChemoControlBField.value,
      [this.targetPHChemoAField.key]: this.targetPHChemoAField.value,
      [this.targetPHChemoBField.key]: this.targetPHChemoBField.value,
      [this.inputTypeChemoAField.key]: this.inputTypeChemoAField.value,
      [this.inputTypeChemoBField.key]: this.inputTypeChemoBField.value,
      [this.pumpDurationChemoAField.key]: this.pumpDurationChemoAField.value,
      [this.pumpDurationChemoBField.key]: this.pumpDurationChemoBField.value,
      [this.flowRatePHAField.key]: this.flowRatePHAField.value,
      [this.flowRatePHBField.key]: this.flowRatePHBField.value,
      // LED controls (always included)
      [this.led1OnField.key]: this.led1OnField.value,
      [this.led2OnField.key]: this.led2OnField.value,
      [this.led3OnField.key]: this.led3OnField.value,
      [this.led1IntensityField.key]: this.led1IntensityField.value,
      [this.led2IntensityField.key]: this.led2IntensityField.value,
      [this.led3IntensityField.key]: this.led3IntensityField.value,
      // Fluorescence controls - only include if useFluorescence is true
      ...(this.useFluorescence()
        ? {
            [this.integrationTimeAField.key]: this.integrationTimeAField.value,
            [this.integrationTimeBField.key]: this.integrationTimeBField.value,
            [this.integrationTimeCField.key]: this.integrationTimeCField.value,
            [this.integrationTimeDField.key]: this.integrationTimeDField.value,
            [this.specGainAField.key]: this.specGainAField.value,
            [this.specGainBField.key]: this.specGainBField.value,
            [this.specGainCField.key]: this.specGainCField.value,
            [this.specGainDField.key]: this.specGainDField.value,
          }
        : {}),
      // Oxygen defaults
      [this.salinityAField.key]: this.salinityAField.value,
      [this.salinityBField.key]: this.salinityBField.value,
      [this.salinityCField.key]: this.salinityCField.value,
      [this.salinityDField.key]: this.salinityDField.value,
      // Sparging defaults
      [this.spargingControlTypeAField.key]:
        this.spargingControlTypeAField.value,
      [this.spargingControlTypeBField.key]:
        this.spargingControlTypeBField.value,
      [this.spargingControlTypeCField.key]:
        this.spargingControlTypeCField.value,
      [this.spargingControlTypeDField.key]:
        this.spargingControlTypeDField.value,
    };
  }

  getLabMembers(): void {
    if (this.labMemembersDropdownLoading()) {
      return;
    }

    const labId = this.laboratory()?.laboratoryId;
    if (labId == null) {
      this.getLabUsersError.set({
        status: 0,
        message: 'Error loading lab members.',
        code: null,
      });
      this.labMemembersDropdownLoading.set(false);
      return;
    }

    this.getLabUsersError.set(null);
    this.labMemembersDropdownLoading.set(true);

    this.labService
      .getLabMembers(labId, true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const { users } = response;
          // Reassign to a new FormBase instance (not mutate) so that the
          // signal input in UnderlinedDropdownFormFieldComponent sees a changed
          // reference and re-renders, allowing mat-select to register the options
          // and match the current form control value.
          this.notificationEmailField = new FormBase<string>({
            ...this.notificationEmailField,
            options: users.map((user: User) => ({
              key: user.email,
              value: user.email,
            })),
          });

          this.users.set(users);
          this.labMemembersDropdownLoading.set(false);

          // Re-apply the current value so mat-select can match it against the
          // now-populated options (value may have been set before options loaded).
          // Use this.f so the correct form is referenced in both set-parameters
          // (experimentControlsForm) and control-experiment (experimentUpdateForm).

          const emailControl = this.f[this.notificationEmailField.key];
          if (!emailControl) return;
          const currentEmail = emailControl.value as string;
          if (currentEmail) {
            emailControl.setValue(currentEmail, { emitEvent: false });
          } else if (this.f[this.enableNotificationsField.key]?.value) {
            emailControl.setValue(this.currentUser()?.email || '', {
              emitEvent: false,
            });
          }
        },
        error: (error: ErrorObject) => {
          this.getLabUsersError.set(error);
          this.labMemembersDropdownLoading.set(false);
        },
      });
  }
}
